<template>
  <div
    class="account-statement"
    :class="{
      'p-4': isGeneralStatement && !embedded,
      'account-statement--embedded': embedded,
    }"
  >
    <!-- Embebido (cuenta corriente general por contacto): la cabecera, las
         tarjetas de totales y los filtros los pone la página que lo contiene. -->
    <template v-if="!embedded">
      <!-- Page Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h6 class="mb-0">
            <i class="fas fa-file-invoice me-2"></i>
            {{
              isGeneralStatement
                ? $t("accountStatementGeneralTitle")
                : $t("Account Statement")
            }}
          </h6>
          <p class="text-muted mb-0">
            <template v-if="isGeneralStatement">
              {{ $t("accountStatementGeneralSubtitle") }}
            </template>
            <template v-else>
              {{ $t("Financial transactions for") }}
              {{ contactName || $t("Contact") }}
            </template>
          </p>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row mt-4">
        <div class="col-md-4">
          <div class="card bg-light">
            <div class="card-body text-center">
              <h6 class="card-title">{{ $t("Total Debits") }}</h6>
              <h4 class="text-danger mb-0">
                {{ formatStatementDebit(getTotalDebits()) }} {{ displayCurrency }}
              </h4>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card bg-light">
            <div class="card-body text-center">
              <h6 class="card-title">{{ $t("Total Credits") }}</h6>
              <h4 class="text-success mb-0">
                {{ formatStatementCredit(getTotalCredits()) }} {{ displayCurrency }}
              </h4>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card bg-light">
            <div class="card-body text-center">
              <!-- Con "desde" el total es el neto del período, sin saldo anterior. -->
              <h6 class="card-title">
                {{
                  startDate
                    ? $t("accountStatementPeriodNet")
                    : $t("Current Balance")
                }}
              </h6>
              <h4
                class="mb-0"
                :class="getFinalBalance() >= 0 ? 'text-success' : 'text-danger'"
              >
                {{ formatStatementSigned(getFinalBalance()) }} {{ displayCurrency }}
              </h4>
            </div>
          </div>
        </div>
      </div>
    </template>
    <!-- Account Statement Table -->
    <div class="card">
      <div class="card-header py-2">
        <!-- Teléfono: la barra de escritorio (pestañas, contacto, dos fechas,
             búsqueda, interruptor y seis botones) apilaba más de 400px antes del
             primer dato. Acá: pestañas en chips, búsqueda con filtros y «⋮», y el
             pedido de pago solo cuando hay algo elegido. -->
        <div v-if="isMobileLayout" class="as-m-toolbar">
          <MobileChips v-if="!embedded" :items="mobileStatementTabs" @select="selectStatementTab($event.key)" />
          <div v-if="filterProformId || filterShippingId" class="d-flex align-items-center gap-2">
            <span class="badge bg-secondary text-wrap text-start">
              <template v-if="filterProformId">{{ $t("Filtered by proforma") }}: {{ filterProformLabel }}</template>
              <template v-else>{{ $t("shipping") }}: {{ filterShippingLabel }}</template>
            </span>
            <button type="button" class="btn btn-outline-secondary btn-sm py-0" @click="clearEntityFilter">
              <i class="fas fa-times"></i> {{ $t("Clear") }}
            </button>
          </div>
          <div class="as-m-toolbar__row">
            <div v-if="!embedded" class="as-m-search">
              <i class="fas fa-search" aria-hidden="true"></i>
              <input
                v-model="searchText"
                type="search"
                :placeholder="$t('Search...')"
                @keyup.enter="applySearchFilter"
                @input="debounceSearch"
              />
              <button v-if="searchText" type="button" :aria-label="$t('Clear search')" @click="clearSearch">
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <MobileIconButton
              v-if="!embedded"
              icon="sliders-h"
              :label="$t('Filters')"
              :active="!!(startDate || endDate)"
              @click="mobileFiltersOpen = true"
            />
            <MobileIconButton icon="ellipsis-v" :label="$t('mobile.actions')" @click="mobileToolsOpen = true" />
          </div>
          <button
            v-if="canMakePayments && statementView === 'active' && selectedPayableIds.length"
            type="button"
            class="btn btn-danger w-100"
            @click="openSendPaymentForSelected"
          >
            <i class="fas fa-arrow-up"></i><i class="fas fa-dollar-sign me-1"></i>
            {{ $t("Send Payment Request") }} ({{ formatNumber(selectedPayableAmount) }})
          </button>
          <button
            v-if="rowHideEnabled && hideableSelectedRowKeys.length > 0"
            type="button"
            class="btn btn-sm w-100"
            :class="hiddenView ? 'btn-secondary' : 'btn-outline-secondary'"
            :disabled="loading || rowHideBusy"
            @click="hideOrShowSelected"
          >
            <i class="fas me-1" :class="hiddenView ? 'fa-eye' : 'fa-eye-slash'"></i>
            {{ hiddenView ? $t("ShowSelected") : $t("HideSelected") }} ({{ hideableSelectedRowKeys.length }})
          </button>
          <MobileBottomSheet v-if="!embedded" v-model="mobileFiltersOpen" :title="$t('Filters')">
            <div class="as-m-filters">
              <BalanceDateRangeFilter
                :from.sync="startDate"
                :to.sync="endDate"
                :from-title="$t('accountStatementFromHint')"
                :to-title="$t('contactBalanceAsOfHint')"
                @change="applyFilters"
              />
              <div class="form-check form-switch mb-0">
                <input
                  id="excludeBalanceZeroSwitchMobile"
                  v-model="excludeBalanceZero"
                  class="form-check-input"
                  type="checkbox"
                  @change="applyFilters"
                />
                <label class="form-check-label" for="excludeBalanceZeroSwitchMobile">{{ $t("Exclude balance 0") }}</label>
              </div>
            </div>
          </MobileBottomSheet>
          <MobileBottomSheet v-model="mobileToolsOpen" :title="$t('mobile.actions')">
            <MobileSheetItem
              v-if="hasCollapsibleStatementGroups"
              :icon="hasCollapsedStatementGroups ? 'expand-alt' : 'compress-alt'"
              :label="hasCollapsedStatementGroups ? $t('accountStatementExpandAll') : $t('accountStatementCollapseAll')"
              @click="mobileToolsOpen = false; toggleAllStatementGroups()"
            />
            <MobileSheetItem
              icon="file-excel"
              :label="$t('Export to Excel')"
              :spin="exportingExcel"
              :disabled="loading || exportingExcel || groupedTransactionsRows.length === 0"
              @click="mobileToolsOpen = false; exportToExcel()"
            />
            <MobileSheetItem icon="sync-alt" :label="$t('Refresh')" @click="mobileToolsOpen = false; refreshData()" />
          </MobileBottomSheet>
        </div>
        <template v-else>
        <div
          v-if="!embedded || filterProformId || filterShippingId"
          class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2"
        >
          <ul v-if="!embedded" class="nav nav-pills mb-0 flex-shrink-0">
            <li class="nav-item">
              <button
                type="button"
                class="nav-link py-1 px-2"
                :class="{ active: statementTab === 'active' }"
                @click="selectStatementTab('active')"
              >
                {{ $t("accountStatementTabMovements") }}
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link py-1 px-2"
                :class="{ active: statementTab === 'archived' }"
                @click="selectStatementTab('archived')"
              >
                {{ $t("accountStatementTabArchived") }}
              </button>
            </li>
            <li v-if="rowHideEnabled" class="nav-item">
              <button
                type="button"
                class="nav-link py-1 px-2"
                :class="{ active: statementTab === 'hidden' }"
                @click="selectStatementTab('hidden')"
              >
                {{ $t("listTitleHidden") }}
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link py-1 px-2"
                :class="{ active: statementTab === 'all' }"
                @click="selectStatementTab('all')"
              >
                {{ $t("accountStatementTabAll") }}
              </button>
            </li>
          </ul>
          <h6 class="mb-0 d-flex flex-wrap align-items-center gap-2">
            <span v-if="!embedded" class="badge bg-primary">{{
              isGeneralStatement
                ? $t("accountStatementAllContacts")
                : contactName || $t("Contact")
            }}</span>
            <template v-if="filterProformId || filterShippingId">
              <span class="badge bg-secondary text-wrap text-start">
                <template v-if="filterProformId">
                  {{ $t("Filtered by proforma") }}: {{ filterProformLabel }}
                </template>
                <template v-else>
                  {{ $t("shipping") }}: {{ filterShippingLabel }}
                </template>
              </span>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm py-0"
                @click="clearEntityFilter"
              >
                <i class="fas fa-times"></i> {{ $t("Clear") }}
              </button>
            </template>
          </h6>
        </div>
        <!-- Embebido, los botones van a la izquierda: el contacto desplegado se estira
             al ancho de su tabla y a la derecha quedaban fuera de la pantalla. -->
        <div
          class="d-flex align-items-center flex-wrap gap-2"
          :class="embedded ? 'justify-content-start' : 'justify-content-between'"
        >
          <!-- Fechas y búsqueda juntas, centradas en el espacio libre. -->
          <div
            v-if="!embedded"
            class="d-flex align-items-center flex-wrap gap-2 flex-grow-1 justify-content-center"
          >
            <BalanceDateRangeFilter
              :from.sync="startDate"
              :to.sync="endDate"
              :from-title="$t('accountStatementFromHint')"
              :to-title="$t('contactBalanceAsOfHint')"
              @change="applyFilters"
            />
            <div class="input-group input-group-sm" style="width: 200px;">
              <span class="input-group-text py-1"><i class="fas fa-search"></i></span>
              <input
                type="text"
                class="form-control py-1"
                :placeholder="$t('Search...')"
                v-model="searchText"
                @keyup.enter="applySearchFilter"
                @input="debounceSearch"
              />
              <button
                v-if="searchText"
                class="btn btn-outline-secondary btn-sm py-1"
                type="button"
                @click="clearSearch"
                :title="$t('Clear search')"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          <div
            class="d-flex flex-wrap align-items-center gap-2"
            :class="{ 'ms-auto': !embedded }"
          >
            <div v-if="!embedded" class="form-check form-switch mb-0">
              <input
                class="form-check-input"
                type="checkbox"
                id="excludeBalanceZeroSwitch"
                v-model="excludeBalanceZero"
                @change="applyFilters"
              />
              <label class="form-check-label small" for="excludeBalanceZeroSwitch">
                {{ $t("Exclude balance 0") }}
              </label>
            </div>
            <button
              v-if="canMakePayments && statementView === 'active'"
              type="button"
              @click="openSendPaymentForSelected"
              class="btn btn-danger btn-sm"
              :disabled="selectedPayableIds.length === 0"
              :title="$t('Add New Payment')"
            >
              <i class="fas fa-arrow-up"></i>
              <i class="fas fa-dollar-sign"></i>
              {{ $t("Send Payment Request")
              }}<template v-if="selectedPayableIds.length"
                >({{ formatNumber(selectedPayableAmount) }})</template
              >
            </button>
            <button
              v-if="hasCollapsibleStatementGroups"
              type="button"
              class="btn btn-outline-secondary btn-sm"
              @click="toggleAllStatementGroups"
              :title="
                hasCollapsedStatementGroups
                  ? $t('accountStatementExpandAll')
                  : $t('accountStatementCollapseAll')
              "
            >
              <i
                class="fas me-1"
                :class="
                  hasCollapsedStatementGroups
                    ? 'fa-expand-alt'
                    : 'fa-compress-alt'
                "
              ></i>
              {{
                hasCollapsedStatementGroups
                  ? $t("accountStatementExpandAll")
                  : $t("accountStatementCollapseAll")
              }}
            </button>
            <button
              v-if="rowHideEnabled && hideableSelectedRowKeys.length > 0"
              type="button"
              class="btn btn-sm"
              :class="hiddenView ? 'btn-secondary' : 'btn-outline-secondary'"
              :disabled="loading || rowHideBusy"
              @click="hideOrShowSelected"
            >
              <i
                class="fas me-1"
                :class="hiddenView ? 'fa-eye' : 'fa-eye-slash'"
              ></i>
              {{ hiddenView ? $t("ShowSelected") : $t("HideSelected") }}
              ({{ hideableSelectedRowKeys.length }})
            </button>
            <button
              type="button"
              @click="exportToExcel"
              class="btn btn-success btn-sm"
              :disabled="loading || exportingExcel || groupedTransactionsRows.length === 0"
              :title="$t('Export to Excel')"
            >
              <i
                class="fas fa-file-excel me-1"
                :class="{ 'fa-spin': exportingExcel }"
              ></i>
              {{ $t("Export to Excel") }}
            </button>
            <button
              type="button"
              class="btn btn-sm"
              :class="
                hiddenStatementColumnCount > 0
                  ? 'btn-secondary'
                  : 'btn-outline-secondary'
              "
              :title="$t('Customize display')"
              :aria-label="$t('Customize display')"
              @click="showColumnSelector = !showColumnSelector"
            >
              <i class="fas fa-columns"></i>
              <template v-if="hiddenStatementColumnCount > 0">
                {{ displayTableColumns.length }}/{{
                  statementColumnCatalog.length
                }}</template
              >
            </button>
            <button type="button" @click="refreshData" class="btn btn-outline-primary btn-sm">
              <i class="fas fa-sync-alt"></i> {{ $t("Refresh") }}
            </button>
          </div>
        </div>
        </template>
      </div>
      <div class="card-body">
        <ColumnSelector
          :show="showColumnSelector"
          :table-fields="statementColumnCatalog"
          :selected-columns="selectedColumns"
          @close="showColumnSelector = false"
          @columns-changed="handleStatementColumnsChanged"
        />

        <div
          v-if="activeStatementFilterChips.length"
          class="d-flex flex-wrap align-items-center gap-2 mb-3"
        >
          <span class="small text-muted">
            <i class="fas fa-filter me-1"></i>{{ $t("Filters") }}:
          </span>
          <span
            v-for="chip in activeStatementFilterChips"
            :key="`as-chip-${chip.key}`"
            class="badge bg-light text-dark border d-inline-flex align-items-center gap-1"
          >
            <span>{{ $t(chip.label) }}: {{ chip.text }}</span>
            <button
              type="button"
              class="btn btn-link btn-sm p-0 lh-1 text-danger"
              :title="$t('Clear filter')"
              :aria-label="$t('Clear filter')"
              @click="clearStatementColumnFilter(chip.key)"
            >
              <i class="fas fa-times" style="font-size: 0.7rem"></i>
            </button>
          </span>
          <button
            v-if="activeStatementFilterChips.length > 1"
            type="button"
            class="btn btn-link btn-sm p-0 text-muted"
            @click="clearAllStatementColumnFilters"
          >
            {{ $t("Clear all") }}
          </button>
        </div>

        <!-- En el teléfono el aviso va como nota chica: el recuadro ocupaba media pantalla. -->
        <p v-if="!embedded && isMobileLayout" class="as-m-hint" role="note">
          <i class="fas fa-info-circle me-1"></i>{{ $t("accountStatementBalanceReadingHint") }}
        </p>
        <div
          v-else-if="!embedded"
          class="alert alert-info account-statement-balance-hint py-2 px-3 mb-3"
          role="note"
        >
          <i class="fas fa-info-circle me-2"></i>
          <span>{{ $t("accountStatementBalanceReadingHint") }}</span>
        </div>
        <ContentSkeleton v-if="loading" variant="table" :rows="8" :cols="7" />
        <div v-else-if="groupedTransactionsRows.length === 0" class="text-center py-4">
          <i class="fas fa-receipt fa-3x text-muted mb-3"></i>
          <p class="text-muted">
            {{
              searchText || hasActiveColumnFilters
                ? $t("No transactions found matching your search")
                : $t("No transactions found")
            }}
          </p>
          <p
            v-if="hiddenByExcludeBalanceZero && excludeBalanceZero"
            class="text-muted small mb-0"
          >
            {{ $t("accountStatementHiddenByExcludeBalanceZero") }}
          </p>
          <!-- En el teléfono el interruptor vive en la hoja de filtros: el aviso
               trae su propio botón para no tener que ir a buscarlo. -->
          <button
            v-if="isMobileLayout && hiddenByExcludeBalanceZero && excludeBalanceZero"
            type="button"
            class="btn btn-outline-primary btn-sm mt-2"
            @click="excludeBalanceZero = false; applyFilters()"
          >
            {{ $t("Show all") }}
          </button>
          <p v-if="searchText && transactions.length > 0" class="text-muted small">
            {{ $t("Try a different search term or clear the search to see all transactions") }}
          </p>
        </div>
        <!-- Teléfono: tarjetas en lugar de la tabla (había que desplazarla de
             costado para ver los montos). Mismo orden y mismas filas que la
             tabla: saldo inicial, grupos de proforma / embarque (se pliegan al
             tocarlos) y movimientos. La casilla de selección queda en la
             tarjeta para armar el pedido de pago; las acciones de cada fila van
             en una hoja, con las mismas condiciones que los botones de escritorio. -->
        <div v-else-if="isMobileLayout" class="as-m-list">
          <template v-for="(row, index) in groupedTransactionsRows">
            <div
              v-if="row.type === 'initial_balance'"
              :key="`as-m-ib-${index}-${row._id}`"
              class="as-m-initial"
            >
              <i class="fas fa-wallet text-info me-1"></i>
              <span>{{ row.name }}</span>
              <span
                class="badge ms-auto"
                :class="row.totals && row.totals.balance >= 0 ? 'bg-success' : 'bg-danger'"
              >{{ $t("Initial Balance") }}: {{ formatStatementSigned(row.totals ? row.totals.balance : 0) }}</span>
            </div>

            <div
              v-else-if="row.type === 'proform_group' || row.type === 'shipping_group'"
              :key="`as-m-${row.groupKey}`"
              class="as-m-row"
              :class="row.type === 'shipping_group' ? 'as-m-row--1' : ''"
            >
              <MobileRecordCard
                :muted="isStatementRowHidden(row)"
                :more-label="$t('mobile.recordActions')"
                @open="toggleStatementGroup(row.groupKey)"
                @more="mobileStatementRow = row"
              >
                <template #title>
                  <span class="as-m-title">
                    <input
                      v-if="showRowSelectionColumn && rowSelectionKeys(row).length && !isFullyRequestedRow(row)"
                      type="checkbox"
                      class="form-check-input as-m-check"
                      :checked="isRowSelected(row)"
                      :indeterminate.prop="isRowSelectionIndeterminate(row)"
                      :disabled="rowHideBusy"
                      @click.stop
                      @change="toggleRowSelection(row)"
                    />
                    <i
                      class="fas text-muted"
                      :class="isStatementGroupCollapsed(row.groupKey) ? 'fa-chevron-right' : 'fa-chevron-down'"
                    ></i>
                    <i class="fas" :class="row.type === 'proform_group' ? 'fa-file-invoice text-primary' : 'fa-ship text-secondary'"></i>
                    <span>{{ row.label }}</span>
                  </span>
                </template>
                <template #badges>
                  <span class="badge rounded-pill" :class="row.type === 'proform_group' ? 'bg-primary' : 'bg-secondary'">{{ row.transactionCount }}</span>
                  <MissingPrepaymentWarningIcon
                    v-if="row.type === 'proform_group' ? showProformPrepaymentWarning(row) : showShippingPrepaymentWarning(row)"
                    :show="true"
                    :record="row.type === 'proform_group' ? row.proformAdvance : row.shippingAdvance"
                  />
                </template>
                <MobileCardLine :label="$t('Balance')" strong>
                  <span :class="statementSignedClass(row.summary.balance)">{{ formatStatementSigned(row.summary.balance) }}</span>
                </MobileCardLine>
                <MobileCardLine v-if="row.summary.totalDebits" :label="$t('Debit')">
                  <span class="text-danger">{{ formatStatementDebit(row.summary.totalDebits) }}</span>
                </MobileCardLine>
                <MobileCardLine v-if="row.summary.totalCredits" :label="$t('Credit')">
                  <span class="text-success">{{ formatStatementCredit(row.summary.totalCredits) }}</span>
                </MobileCardLine>
                <MobileCardLine v-if="row.summary.etd || row.summary.eta" :label="$t('ETD') + ' / ' + $t('ETA')">
                  {{ row.summary.etd || "—" }} → {{ row.summary.eta || "—" }}
                </MobileCardLine>
              </MobileRecordCard>
            </div>

            <div
              v-else
              :key="`as-m-tx-${index}-${row._id || (row.data && row.data._id) || index}`"
              class="as-m-row as-m-row--2"
            >
              <MobileRecordCard
                :muted="isStatementRowHidden(row)"
                :more-label="$t('mobile.recordActions')"
                @open="mobileStatementRow = row"
                @more="mobileStatementRow = row"
              >
                <template #title>
                  <span class="as-m-title">
                    <input
                      v-if="showRowSelectionColumn && rowSelectionKeys(row).length && !isFullyRequestedRow(row)"
                      type="checkbox"
                      class="form-check-input as-m-check"
                      :checked="isRowSelected(row)"
                      :indeterminate.prop="isRowSelectionIndeterminate(row)"
                      :disabled="rowHideBusy"
                      @click.stop
                      @change="toggleRowSelection(row)"
                    />
                    <span>{{ row.reference || (row.type === "expense" ? getTypeLabel(row.expenseType) : getTransactionTypeLabel(row.type)) }}</span>
                  </span>
                </template>
                <template #badges>
                  <span :class="getTransactionTypeClass(row.type)" class="badge">
                    {{ getStatementTypeLabel(row) }}
                  </span>
                  <span v-if="row.status" :class="getStatusClass(row.status)">{{ getStatusLabel(row.status) }}</span>
                </template>
                <MobileCardLine v-if="isStatementDebitRow(row)" :label="$t('Debit')" strong>
                  <span class="text-danger">{{ formatStatementDebit(statementDebitDisplayAmount(row)) }}</span>
                </MobileCardLine>
                <MobileCardLine v-if="isStatementCreditRow(row)" :label="$t('Credit')" strong>
                  <span class="text-success">{{ formatStatementCredit(row.amount) }}</span>
                </MobileCardLine>
                <MobileCardLine v-if="!row.excludedFromTotals && row.type !== 'pending_prepayment'" :label="$t('Balance')">
                  <span :class="statementSignedClass(row.runningBalance)">{{ formatStatementSigned(row.runningBalance) }}</span>
                </MobileCardLine>
                <MobileCardLine v-if="row.date" :label="$t('Date')">{{ formatDate(row.date) }}</MobileCardLine>
                <MobileCardLine v-if="getShippingDisplay(row) !== '-'" :label="$t('Shipping')">{{ getShippingDisplay(row) }}</MobileCardLine>
                <MobileCardLine v-if="isGeneralStatement && row.contact" :label="$t('Contact')">{{ row.contact }}</MobileCardLine>
                <MobileCardLine v-if="row.comment" :label="$t('Comment')">{{ row.comment }}</MobileCardLine>
              </MobileRecordCard>
            </div>
          </template>

          <MobileBottomSheet
            :value="!!mobileStatementRow"
            :title="mobileStatementRow ? (mobileStatementRow.label || mobileStatementRow.reference || getTransactionTypeLabel(mobileStatementRow.type)) : ''"
            @input="(open) => { if (!open) mobileStatementRow = null; }"
          >
            <template v-if="mobileStatementRow">
              <MobileSheetItem
                v-if="mobileStatementRow.type === 'payment' || mobileStatementRow.type === 'collect'"
                icon="eye"
                :label="$t('View details')"
                @click="mobileStatementDo(viewPaymentOrCollect)"
              />
              <MobileSheetItem
                v-if="mobileStatementRow.type === 'proform_group' ? !!mobileStatementRow.entityId : normalizeEntityId(getProformId(mobileStatementRow)) && mobileStatementRow.type !== 'shipping_group'"
                icon="file-invoice"
                :label="$t('Proform') + ': ' + (mobileStatementRow.type === 'proform_group' ? mobileStatementRow.label : getProformDisplay(mobileStatementRow))"
                @click="mobileStatementDo(openProformInNewTab)"
              />
              <MobileSheetItem
                v-if="mobileStatementRow.type === 'shipping_group' ? !!mobileStatementRow.entityId : (mobileStatementRow.type !== 'proform_group' && (normalizeEntityId(getShippingId(mobileStatementRow)) || mobileStatementRow.type === 'shipping_pending'))"
                icon="ship"
                :label="$t('Shipping') + ': ' + (mobileStatementRow.type === 'shipping_group' ? mobileStatementRow.label : getShippingDisplay(mobileStatementRow))"
                @click="mobileStatementDo(openShippingInNewTab)"
              />
              <MobileSheetItem
                v-if="mobileStatementRow.contact && mobileStatementRow.type !== 'proform_group' && mobileStatementRow.type !== 'shipping_group'"
                icon="user"
                :label="mobileStatementRow.contact"
                @click="mobileStatementDo(viewContact)"
              />
              <MobileSheetItem
                v-if="statementFileHref(mobileStatementRow.file)"
                icon="paperclip"
                :label="$t('View file')"
                :href="statementFileHref(mobileStatementRow.file)"
                new-tab
              />
              <MobileSheetItem
                v-if="rowHideEnabled && rowHideKeys(mobileStatementRow).length"
                :icon="isStatementRowHidden(mobileStatementRow) ? 'eye' : 'eye-slash'"
                :label="isStatementRowHidden(mobileStatementRow) ? $t('showInThisView') : $t('hideInThisView')"
                :disabled="rowHideBusy"
                separated
                @click="mobileStatementDo(toggleRowHide)"
              />
              <MobileSheetItem
                v-if="mobileStatementRow.legacyProformArchive"
                icon="undo"
                :label="$t('accountStatementRestoreOperation')"
                @click="mobileStatementDo(confirmRestoreRow)"
              />
            </template>
          </MobileBottomSheet>
        </div>
        <div v-else class="table-responsive as-table-scroll">
          <table class="table table-hover account-statement-table">
            <colgroup>
              <col v-if="showRowSelectionColumn" class="as-col-selection" />
              <col
                v-for="col in displayTableColumns"
                :key="`as-colgroup-${col.key}`"
                :class="statementColClass(col.key)"
                :style="listTableColStyle(col.key)"
              />
              <col v-if="showStatementActionsColumn" class="as-col-actions" />
            </colgroup>
            <thead>
              <tr class="text-center">
                <th v-if="showRowSelectionColumn" width="48">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="isAllRowsSelected"
                    :indeterminate.prop="isRowSelectionHeaderIndeterminate"
                    :disabled="
                      loading ||
                      rowHideBusy ||
                      selectingAllRows ||
                      !selectableRowKeys.length
                    "
                    :title="$t('SelectAll')"
                    :aria-label="$t('SelectAll')"
                    @change="toggleSelectAllRows"
                  />
                </th>
                <th
                  v-for="(col, colIndex) in displayTableColumns"
                  :key="`as-th-${col.key}`"
                  class="as-th-draggable"
                  :class="{
                    'sorted-asc':
                      statementSortColumn === col.key &&
                      statementSortDirection === 'asc',
                    'sorted-desc':
                      statementSortColumn === col.key &&
                      statementSortDirection === 'desc',
                    'list-col-pinned': isColumnPinned(col.key),
                    'list-col-pinned-boundary': isDataColPinnedBoundary(
                      col,
                      colIndex
                    ),
                  }"
                  :style="listDataColHeaderStickyStyle(col, colIndex)"
                  :draggable="!isResizing"
                  @dragstart="onDragStart($event, colIndex)"
                  @dragover="onDragOver($event)"
                  @drop="onDrop($event, colIndex)"
                  @dragenter="onDragEnter($event)"
                  @dragleave="onDragLeave($event)"
                >
                  <div
                    class="d-inline-flex align-items-center justify-content-center gap-1 flex-nowrap"
                  >
                    <button
                      type="button"
                      class="btn btn-link btn-sm py-0 px-1 column-header-toolbar-pin-btn"
                      :class="{
                        'column-header-toolbar-pin-btn--active': isColumnPinned(
                          col.key
                        ),
                      }"
                      :title="
                        isColumnPinned(col.key)
                          ? $t('UnpinColumn')
                          : $t('PinColumn')
                      "
                      :aria-label="
                        isColumnPinned(col.key)
                          ? $t('UnpinColumn')
                          : $t('PinColumn')
                      "
                      @click.stop="togglePinColumn(col.key)"
                    >
                      <i class="fas fa-thumbtack" style="font-size: 0.8rem"></i>
                    </button>
                    <i
                      v-if="isStatementColumnSortable(col)"
                      class="cursor-pointer column-header-sort-icon"
                      :class="
                        statementSortColumn === col.key
                          ? statementSortDirection === 'asc'
                            ? 'fas fa-sort-up'
                            : 'fas fa-sort-down'
                          : 'fas fa-sort'
                      "
                      :title="$t('Sort')"
                      @click.stop="toggleStatementSort(col)"
                    ></i>
                    <span>{{ $t(statementColumnLabel(col)) }}</span>
                    <button
                      v-if="isStatementColumnFilterable(col)"
                      type="button"
                      class="btn btn-link btn-sm py-0 px-1 column-header-toolbar-filter-btn"
                      :class="{
                        'column-header-toolbar-filter-btn--active':
                          isStatementColumnFilterActive(col.key),
                      }"
                      :title="$t('Filters')"
                      :aria-label="$t('Filters')"
                      @click.stop="openStatementColumnFilter(col.key)"
                    >
                      <i class="fas fa-filter" style="font-size: 0.8rem"></i>
                    </button>
                  </div>
                  <div
                    class="as-resize-handle"
                    :title="$t('ResizeColumn')"
                    @mousedown.stop="startResize($event, col.key)"
                    @click.stop
                  ></div>
                </th>
                <th v-if="showStatementActionsColumn" class="text-center">
                  {{ $t("accountStatementActions") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(row, index) in groupedTransactionsRows">
                <!-- ============ Saldo inicial ============ -->
                <tr
                  v-if="row.type === 'initial_balance'"
                  :key="`initial-balance-${index}-${row._id}`"
                  class="bg-info bg-opacity-10 text-center fw-bold"
                >
                  <td v-if="showRowSelectionColumn"></td>
                  <td
                    v-for="(col, colIndex) in displayTableColumns"
                    :key="`as-ib-${col.key}`"
                    :class="statementPlainCellClass(col, colIndex)"
                    :style="listDataColStickyStyle(col, colIndex)"
                  >
                    <template v-if="col.key === 'proform'">
                      <span class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'shipping'">
                      <div class="d-flex align-items-center gap-2">
                        <i class="fas fa-wallet text-info me-2"></i>
                        <span>{{ row.name }}</span>
                      </div>
                    </template>
                    <template v-else-if="col.key === 'debit'">
                      <span
                        v-if="row.totals && row.totals.balance < 0"
                        class="text-danger fw-bold"
                      >
                        {{ formatStatementDebit(row.totals.balance) }}
                      </span>
                      <span v-else>-</span>
                    </template>
                    <template v-else-if="col.key === 'credit'">
                      <span
                        v-if="row.totals && row.totals.balance >= 0"
                        class="text-success fw-bold"
                      >
                        {{ formatStatementCredit(row.totals.balance) }}
                      </span>
                      <span v-else>-</span>
                    </template>
                    <template v-else-if="col.key === 'balance'">
                      <span
                        class="badge"
                        :class="
                          row.totals && row.totals.balance >= 0
                            ? 'bg-success'
                            : 'bg-danger'
                        "
                      >
                        {{ $t("Initial Balance") }}:
                        {{
                          formatStatementSigned(
                            row.totals ? row.totals.balance : 0
                          )
                        }}
                      </span>
                    </template>
                    <template v-else-if="col.key === 'accumulatedBalance'">
                      <span
                        v-if="hasStatementAccumulatedBalance(row)"
                        :class="statementSignedClass(row.accumulatedBalance)"
                      >
                        {{ formatStatementSigned(row.accumulatedBalance) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else>-</template>
                  </td>
                  <td v-if="showStatementActionsColumn"></td>
                </tr>

                <!-- ============ Grupo de proforma / de shipping ============ -->
                <tr
                  v-else-if="
                    row.type === 'proform_group' || row.type === 'shipping_group'
                  "
                  :key="row.groupKey"
                  class="statement-group-row"
                  :class="[
                    row.type === 'proform_group'
                      ? 'statement-proform-group'
                      : 'statement-shipping-group',
                    {
                      'is-expanded': !isStatementGroupCollapsed(row.groupKey),
                      'per-view-row-hidden': isStatementRowHidden(row),
                    },
                  ]"
                  role="button"
                  tabindex="0"
                  :aria-expanded="String(!isStatementGroupCollapsed(row.groupKey))"
                  @click="toggleStatementGroup(row.groupKey)"
                  @keydown.enter.prevent="toggleStatementGroup(row.groupKey)"
                  @keydown.space.prevent="toggleStatementGroup(row.groupKey)"
                >
                  <td v-if="showRowSelectionColumn" @click.stop>
                    <!-- Deshabilitado no dispara hover: el title va en el wrapper. -->
                    <span
                      v-if="isFullyRequestedRow(row)"
                      class="d-inline-block"
                      :title="requestedRowTooltip(row)"
                    >
                      <input
                        type="checkbox"
                        class="form-check-input"
                        disabled
                      />
                    </span>
                    <input
                      v-else-if="rowSelectionKeys(row).length"
                      type="checkbox"
                      class="form-check-input"
                      :checked="isRowSelected(row)"
                      :indeterminate.prop="isRowSelectionIndeterminate(row)"
                      :disabled="rowHideBusy"
                      @change="toggleRowSelection(row)"
                    />
                  </td>
                  <td
                    v-for="(col, colIndex) in displayTableColumns"
                    :key="`as-grp-${col.key}`"
                    :class="statementGroupCellClass(col, colIndex, row.type)"
                    :style="listDataColStickyStyle(col, colIndex)"
                  >
                    <!-- Las dos primeras columnas llevan el título del grupo;
                         cuál de las dos depende del tipo de grupo. -->
                    <template v-if="col.key === 'contact'">
                      <span class="statement-summary-value">{{
                        row.summary.contacts
                      }}</span>
                    </template>
                    <template v-else-if="col.key === 'proform'">
                      <div
                        v-if="row.type === 'proform_group'"
                        class="statement-collapsed-title"
                      >
                        <i
                          class="fas statement-group-chevron"
                          :class="
                            isStatementGroupCollapsed(row.groupKey)
                              ? 'fa-chevron-right'
                              : 'fa-chevron-down'
                          "
                        ></i>
                        <i class="fas fa-file-invoice text-primary"></i>
                        <span class="statement-group-label">{{ row.label }}</span>
                        <span class="badge bg-primary rounded-pill">
                          {{ row.transactionCount }}
                        </span>
                        <button
                          v-if="row.entityId"
                          type="button"
                          class="btn btn-link btn-sm p-0 text-secondary"
                          @click.stop="openProformInNewTab(row)"
                          :title="$t('View details')"
                        >
                          <i class="fas fa-external-link-alt"></i>
                        </button>
                        <MissingPrepaymentWarningIcon
                          v-if="showProformPrepaymentWarning(row)"
                          :show="true"
                          :record="row.proformAdvance"
                        />
                      </div>
                      <span v-else class="text-muted">{{ row.proformName }}</span>
                    </template>
                    <template v-else-if="col.key === 'shipping'">
                      <div
                        v-if="row.type === 'shipping_group'"
                        class="statement-collapsed-title statement-shipping-collapsed-title"
                      >
                        <i
                          class="fas statement-group-chevron"
                          :class="
                            isStatementGroupCollapsed(row.groupKey)
                              ? 'fa-chevron-right'
                              : 'fa-chevron-down'
                          "
                        ></i>
                        <i class="fas fa-ship text-secondary"></i>
                        <span class="statement-group-label">{{ row.label }}</span>
                        <span class="badge bg-secondary rounded-pill">
                          {{ row.transactionCount }}
                        </span>
                        <button
                          v-if="row.entityId"
                          type="button"
                          class="btn btn-link btn-sm p-0 text-secondary"
                          @click.stop="openShippingInNewTab(row)"
                          :title="$t('View details')"
                        >
                          <i class="fas fa-external-link-alt"></i>
                        </button>
                        <MissingPrepaymentWarningIcon
                          v-if="showShippingPrepaymentWarning(row)"
                          :show="true"
                          :record="row.shippingAdvance"
                        />
                        <button
                          v-if="rowHideEnabled && rowHideKeys(row).length"
                          type="button"
                          class="btn btn-link btn-sm p-0 text-secondary"
                          :disabled="rowHideBusy"
                          :title="
                            isStatementRowHidden(row)
                              ? $t('showInThisView')
                              : $t('hideInThisView')
                          "
                          @click.stop="toggleRowHide(row)"
                        >
                          <i
                            class="fas"
                            :class="
                              isStatementRowHidden(row)
                                ? 'fa-eye'
                                : 'fa-eye-slash'
                            "
                          ></i>
                        </button>
                      </div>
                      <template v-else>
                        <span
                          v-if="row.summary.shippingCount"
                          class="statement-summary-value"
                        >
                          <i class="fas fa-ship me-1"></i
                          >{{ row.summary.shippingCount }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </template>
                    </template>
                    <template v-else-if="col.key === 'reference'">
                      <span
                        class="as-reference-text"
                        :title="
                          row.summary.referencesTitle || row.summary.references
                        "
                      >
                        {{ row.summary.references }}
                      </span>
                    </template>
                    <template v-else-if="col.key === 'comment'">
                      <span
                        v-if="row.summary.commentCount"
                        class="statement-summary-value"
                        :title="row.summary.commentsTitle"
                      >
                        <i class="fas fa-comment me-1"></i
                        >{{ row.summary.commentCount }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'file'">
                      <span
                        v-if="row.summary.fileCount"
                        class="statement-summary-value"
                      >
                        <i class="fas fa-paperclip me-1"></i>
                        {{
                          $tc("accountStatementFilesCount", row.summary.fileCount, {
                            count: row.summary.fileCount,
                          })
                        }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'debit'">
                      {{
                        row.summary.totalDebits
                          ? formatStatementDebit(row.summary.totalDebits)
                          : "-"
                      }}
                    </template>
                    <template v-else-if="col.key === 'credit'">
                      {{
                        row.summary.totalCredits
                          ? formatStatementCredit(row.summary.totalCredits)
                          : "-"
                      }}
                    </template>
                    <template v-else-if="col.key === 'balance'">
                      <span :class="statementSignedClass(row.summary.balance)">
                        {{ formatStatementSigned(row.summary.balance) }}
                      </span>
                    </template>
                    <template v-else-if="col.key === 'accumulatedBalance'">
                      <span
                        :class="
                          statementSignedClass(row.summary.accumulatedBalance)
                        "
                      >
                        {{ formatStatementSigned(row.summary.accumulatedBalance) }}
                      </span>
                    </template>
                    <template v-else-if="col.key === 'shippingDate'">{{
                      row.summary.shippingDate
                    }}</template>
                    <template v-else-if="col.key === 'etd'">{{
                      row.summary.etd
                    }}</template>
                    <template v-else-if="col.key === 'eta'">{{
                      row.summary.eta
                    }}</template>
                    <template v-else-if="col.key === 'transitDays'">{{
                      row.summary.transitDays
                    }}</template>
                    <template v-else-if="col.key === 'date'">{{
                      row.summary.date
                    }}</template>
                    <template v-else-if="col.key === 'status'">{{
                      row.summary.statuses
                    }}</template>
                    <template v-else-if="col.key === 'type'">{{
                      row.summary.types
                    }}</template>
                  </td>
                  <td v-if="showStatementActionsColumn"></td>
                </tr>

                <!-- ============ Movimiento ============ -->
                <tr
                  v-else
                  :key="`transaction-${index}-${row._id || row.data?._id || index}`"
                  class="text-center hover:bg-light"
                  :class="[
                    rowHighlightClass(row),
                    { 'per-view-row-hidden': isStatementRowHidden(row) },
                  ]"
                  @mouseleave="onStatementTransactionRowLeave"
                >
                  <td v-if="showRowSelectionColumn" class="align-middle" @click.stop>
                    <!-- Deshabilitado no dispara hover: el title va en el wrapper. -->
                    <span
                      v-if="isFullyRequestedRow(row)"
                      class="d-inline-block"
                      :title="requestedRowTooltip(row)"
                    >
                      <input
                        type="checkbox"
                        class="form-check-input"
                        disabled
                      />
                    </span>
                    <input
                      v-else-if="rowSelectionKeys(row).length"
                      type="checkbox"
                      class="form-check-input"
                      :checked="isRowSelected(row)"
                      :disabled="rowHideBusy"
                      @change="toggleRowSelection(row)"
                    />
                  </td>
                  <td
                    v-for="(col, colIndex) in displayTableColumns"
                    :key="`as-tx-${col.key}`"
                    :class="statementCellClass(col, colIndex)"
                    :style="listDataColStickyStyle(col, colIndex)"
                    @mouseenter="onStatementCellEnter(col.key, row)"
                  >
                    <template v-if="col.key === 'contact'">
                      <span
                        v-if="row.contact"
                        @click.stop="viewContact(row)"
                        class="text-primary fw-bold cursor-pointer"
                        style="cursor: pointer; text-decoration: underline"
                        :title="row.contact"
                      >
                        {{ row.contact }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'proform'">
                      <div
                        class="d-flex align-items-center justify-content-center gap-1 flex-wrap"
                      >
                        <span
                          v-if="getProformDisplay(row) !== '-'"
                          @click.stop="toggleProformFilter(row)"
                          class="text-primary fw-bold cursor-pointer"
                          style="cursor: pointer; text-decoration: underline"
                        >
                          {{ getProformDisplay(row) }}
                        </span>
                        <span v-else class="text-muted">-</span>
                        <button
                          v-if="normalizeEntityId(getProformId(row))"
                          type="button"
                          class="btn btn-link btn-sm p-0 lh-1 text-secondary"
                          @click.stop="openProformInNewTab(row)"
                          :title="$t('View details')"
                        >
                          <i
                            class="fas fa-external-link-alt"
                            style="font-size: 0.75rem"
                          ></i>
                        </button>
                        <MissingPrepaymentWarningIcon
                          v-if="showProformPrepaymentWarning(row)"
                          :show="true"
                          :record="row.proformAdvance"
                        />
                      </div>
                    </template>
                    <template v-else-if="col.key === 'shipping'">
                      <div
                        class="d-flex align-items-center justify-content-center gap-1 flex-wrap"
                      >
                        <span
                          v-if="
                            normalizeEntityId(getShippingId(row)) ||
                            getShippingDisplay(row) !== '-'
                          "
                          @click.stop="toggleShippingFilter(row)"
                          class="text-primary fw-bold cursor-pointer"
                          style="cursor: pointer; text-decoration: underline"
                        >
                          {{ getShippingDisplay(row) }}
                        </span>
                        <span v-else class="text-muted">-</span>
                        <button
                          v-if="
                            normalizeEntityId(getShippingId(row)) ||
                            row.type === 'shipping_pending'
                          "
                          type="button"
                          class="btn btn-link btn-sm p-0 lh-1 text-secondary"
                          @click.stop="openShippingInNewTab(row)"
                          :title="$t('View details')"
                        >
                          <i
                            class="fas fa-external-link-alt"
                            style="font-size: 0.75rem"
                          ></i>
                        </button>
                        <MissingPrepaymentWarningIcon
                          v-if="showShippingPrepaymentWarning(row)"
                          :show="true"
                          :record="row.shippingAdvance"
                        />
                        <span
                          :class="getTransactionTypeClass(row.type)"
                          class="badge ms-2"
                        >
                          {{ getStatementTypeLabel(row) }}
                        </span>
                        <button
                          v-if="row.type === 'payment' || row.type === 'collect'"
                          @click="viewPaymentOrCollect(row)"
                          class="btn btn-sm btn-outline-secondary p-1 ms-2"
                          :title="$t('View details')"
                          style="
                            width: 24px;
                            height: 24px;
                            padding: 0;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                          "
                        >
                          <i class="fas fa-eye" style="font-size: 10px"></i>
                        </button>
                      </div>
                    </template>
                    <template v-else-if="col.key === 'shippingDate'">{{
                      formatStatementShippingDate(row)
                    }}</template>
                    <template v-else-if="col.key === 'etd'">{{
                      formatStatementEtd(row)
                    }}</template>
                    <template v-else-if="col.key === 'eta'">{{
                      formatStatementEta(row)
                    }}</template>
                    <template v-else-if="col.key === 'transitDays'">{{
                      formatStatementTransitDays(row)
                    }}</template>
                    <template v-else-if="col.key === 'date'">{{
                      formatDate(row.date)
                    }}</template>
                    <template v-else-if="col.key === 'reference'">
                      <span
                        v-if="row.reference"
                        class="as-reference-text text-muted"
                        :title="row.reference"
                      >
                        {{ row.reference }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'comment'">
                      <span
                        v-if="row.comment"
                        class="text-muted"
                        :title="row.comment"
                      >
                        <i class="fas fa-comment me-1"></i>
                        {{
                          row.comment.length > 30
                            ? row.comment.substring(0, 30) + "..."
                            : row.comment
                        }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'file'">
                      <a
                        v-if="statementFileHref(row.file)"
                        :href="statementFileHref(row.file)"
                        target="_blank"
                        :title="$t('View file')"
                      >
                        <i class="fas fa-file-alt"></i>
                        ...{{ statementFileTail(row.file) }}
                      </a>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'status'">
                      <span
                        :class="getStatusClass(row.status)"
                        :title="getStatusTooltip(row.status)"
                      >
                        <i :class="getStatusIcon(row.status)"></i>
                        {{ getStatusLabel(row.status) }}
                      </span>
                    </template>
                    <template v-else-if="col.key === 'type'">
                      <span
                        v-if="
                          getPaymentExpenseTypeLabel(row) ||
                          getStatementTypeLabel(row)
                        "
                        class="badge bg-info"
                      >
                        {{
                          getPaymentExpenseTypeLabel(row) ||
                          getStatementTypeLabel(row)
                        }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'debit'">
                      <span
                        v-if="isStatementDebitRow(row)"
                        class="text-danger fw-bold"
                      >
                        {{ formatStatementDebit(statementDebitDisplayAmount(row)) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'credit'">
                      <span
                        v-if="isStatementCreditRow(row)"
                        class="text-success fw-bold"
                      >
                        {{ formatStatementCredit(row.amount) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                    <template v-else-if="col.key === 'balance'">
                      <span
                        v-if="
                          !row.excludedFromTotals &&
                          row.type !== 'pending_prepayment'
                        "
                        :class="statementSignedClass(row.runningBalance)"
                      >
                        {{ formatStatementSigned(row.runningBalance) }}
                      </span>
                      <span v-else class="text-muted">—</span>
                    </template>
                    <template v-else-if="col.key === 'accumulatedBalance'">
                      <span
                        v-if="hasStatementAccumulatedBalance(row)"
                        :class="statementSignedClass(row.accumulatedBalance)"
                      >
                        {{ formatStatementSigned(row.accumulatedBalance) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </template>
                  </td>
                  <td
                    v-if="showStatementActionsColumn"
                    class="align-middle text-center"
                  >
                    <button
                      v-if="rowHideEnabled && rowHideKeys(row).length"
                      type="button"
                      class="btn btn-outline-secondary btn-sm py-0 me-1"
                      :disabled="rowHideBusy"
                      :title="
                        isStatementRowHidden(row)
                          ? $t('showInThisView')
                          : $t('hideInThisView')
                      "
                      @click="toggleRowHide(row)"
                    >
                      <i
                        class="fas"
                        :class="
                          isStatementRowHidden(row) ? 'fa-eye' : 'fa-eye-slash'
                        "
                      ></i>
                    </button>
                    <button
                      v-if="row.legacyProformArchive"
                      type="button"
                      class="btn btn-outline-primary btn-sm py-0"
                      @click="confirmRestoreRow(row)"
                    >
                      {{ $t("accountStatementRestoreOperation") }}
                    </button>
                    <span
                      v-else-if="!(rowHideEnabled && rowHideKeys(row).length)"
                      class="text-muted"
                      >—</span
                    >
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ============ Gastos en etapa proforma (provisorios) ============
         Todavía no son factura: quedan fuera de débito/crédito y del saldo.
         Mismas columnas informativas que el extracto, con un único importe.
         La casilla suma su anticipo pendiente al pedido de pago. -->
    <div
      v-if="!embedded && proformSupplierTransactions.length > 0"
      class="card mt-4"
    >
      <div class="card-header d-flex align-items-center gap-2">
        <i class="fas fa-file-signature text-secondary"></i>
        <span class="fw-bold">{{ $t("accountStatementProformSupplierTitle") }}</span>
        <span class="badge bg-secondary rounded-pill">{{
          proformSupplierTransactions.length
        }}</span>
        <small class="text-muted ms-2">{{
          $t("accountStatementProformSupplierHint")
        }}</small>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover account-statement-table mb-0">
            <thead>
              <tr class="text-center">
                <th v-if="showPayableSelection" style="width: 1%"></th>
                <th
                  v-for="col in proformSupplierColumns"
                  :key="`ps-th-${col.key}`"
                >
                  {{ $t(statementColumnLabel(col)) }}
                </th>
                <th class="text-end">{{ $t("Amount") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in proformSupplierTransactions"
                :key="`ps-row-${index}-${row._id}`"
                class="text-center"
              >
                <td v-if="showPayableSelection" class="align-middle">
                  <!-- Deshabilitado no dispara hover: el title va en el wrapper. -->
                  <span
                    v-if="isFullyRequestedExpenseRow(row)"
                    class="d-inline-block"
                    :title="
                      row.proformAdvanceCovered
                        ? $t('accountStatementProformAdvanceCoveredHint')
                        : requestedRowTooltip(row)
                    "
                  >
                    <input type="checkbox" class="form-check-input" disabled />
                  </span>
                  <input
                    v-else-if="isPayableExpenseRow(row)"
                    type="checkbox"
                    class="form-check-input"
                    :checked="isProformSupplierRowSelected(row)"
                    :title="
                      $t('accountStatementProformSupplierRequestHint', {
                        amount: formatNumber(getRowRequestableAmount(row)),
                      })
                    "
                    @change="toggleProformSupplierRowSelection(row)"
                  />
                </td>
                <td
                  v-for="col in proformSupplierColumns"
                  :key="`ps-td-${col.key}`"
                  class="align-middle"
                >
                  <template v-if="col.key === 'proform'">
                    <span
                      v-if="getProformDisplay(row) !== '-'"
                      class="text-primary fw-bold cursor-pointer"
                      style="cursor: pointer; text-decoration: underline"
                      @click.stop="openProformInNewTab(row)"
                      >{{ getProformDisplay(row) }}</span
                    >
                    <span v-else class="text-muted">-</span>
                  </template>
                  <template v-else-if="col.key === 'shipping'">
                    <span
                      v-if="getShippingDisplay(row) !== '-'"
                      class="text-primary fw-bold cursor-pointer"
                      style="cursor: pointer; text-decoration: underline"
                      @click.stop="openShippingInNewTab(row)"
                      >{{ getShippingDisplay(row) }}</span
                    >
                    <span v-else class="text-muted">-</span>
                  </template>
                  <template v-else-if="col.key === 'etd'">{{
                    formatStatementEtd(row)
                  }}</template>
                  <template v-else-if="col.key === 'eta'">{{
                    formatStatementEta(row)
                  }}</template>
                  <template v-else-if="col.key === 'transitDays'">{{
                    formatStatementTransitDays(row)
                  }}</template>
                  <template v-else-if="col.key === 'date'">{{
                    formatDate(row.date)
                  }}</template>
                  <template v-else-if="col.key === 'reference'">
                    <span v-if="row.reference" class="text-muted" :title="row.reference">{{
                      row.reference
                    }}</span>
                    <span v-else class="text-muted">-</span>
                  </template>
                  <template v-else-if="col.key === 'comment'">
                    <span v-if="row.comment" class="text-muted" :title="row.comment">
                      <i class="fas fa-comment me-1"></i>
                      {{
                        row.comment.length > 30
                          ? row.comment.substring(0, 30) + "..."
                          : row.comment
                      }}
                    </span>
                    <span v-else class="text-muted">-</span>
                  </template>
                  <template v-else-if="col.key === 'file'">
                    <a
                      v-if="statementFileHref(row.file)"
                      :href="statementFileHref(row.file)"
                      target="_blank"
                      :title="$t('View file')"
                    >
                      <i class="fas fa-file-alt"></i>
                      ...{{ statementFileTail(row.file) }}
                    </a>
                    <span v-else class="text-muted">-</span>
                  </template>
                  <template v-else-if="col.key === 'status'">
                    <span
                      :class="getStatusClass(row.status)"
                      :title="getStatusTooltip(row.status)"
                    >
                      <i :class="getStatusIcon(row.status)"></i>
                      {{ getStatusLabel(row.status) }}
                    </span>
                  </template>
                  <template v-else-if="col.key === 'type'">
                    <span
                      v-if="getPaymentExpenseTypeLabel(row)"
                      class="badge bg-secondary"
                      >{{ getPaymentExpenseTypeLabel(row) }}</span
                    >
                    <span v-else class="text-muted">-</span>
                  </template>
                  <template v-else>
                    <span class="text-muted">-</span>
                  </template>
                </td>
                <td class="align-middle text-end fw-bold">
                  {{ formatNumber(Math.abs(Number(row.amount) || 0)) }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="fw-bold">
                <td
                  :colspan="proformSupplierColumns.length + (showPayableSelection ? 1 : 0)"
                  class="text-end align-middle"
                >
                  {{ $t("Total") }}
                </td>
                <td class="text-end align-middle">
                  {{ formatNumber(Math.abs(Number(proformSupplierTotal) || 0)) }}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Paginación: el extracto general trae páginas de 20 en vez de ir
         apilando filas, que con todos los contactos se hacía muy pesado. -->
    <div
      v-if="isGeneralStatement && statementTotalCount > 0"
      class="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3"
    >
      <span class="small text-muted">
        {{
          $t("accountStatementShowingRange", {
            from: statementRangeFrom,
            to: statementRangeTo,
            total: statementTotalCount,
          })
        }}
      </span>
      <nav v-if="statementTotalPages > 1" :aria-label="$t('Pagination')">
        <ul class="pagination pagination-sm mb-0">
          <li
            class="page-item"
            :class="{ disabled: loading || currentPage <= 1 }"
          >
            <button
              type="button"
              class="page-link"
              :disabled="loading || currentPage <= 1"
              :title="$t('Previous')"
              :aria-label="$t('Previous')"
              @click="goToStatementPage(currentPage - 1)"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
          </li>
          <li
            v-for="page in statementPageWindow"
            :key="`as-page-${page}`"
            class="page-item"
            :class="{ active: page === currentPage }"
          >
            <button
              type="button"
              class="page-link"
              :disabled="loading"
              @click="goToStatementPage(page)"
            >
              {{ page }}
            </button>
          </li>
          <li
            class="page-item"
            :class="{ disabled: loading || currentPage >= statementTotalPages }"
          >
            <button
              type="button"
              class="page-link"
              :disabled="loading || currentPage >= statementTotalPages"
              :title="$t('Next')"
              :aria-label="$t('Next')"
              @click="goToStatementPage(currentPage + 1)"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Load More Button (extracto de contacto) -->
    <div
      v-if="!isGeneralStatement && pagination && pagination.hasNextPage"
      class="row mt-4"
    >
      <div class="col-12 text-center">
        <button
          class="btn btn-primary"
          @click="loadMore"
          :disabled="loadingMore"
        >
          <span v-if="loadingMore">
            <i class="fas fa-spinner fa-spin me-2"></i>
            {{ $t("Loading...") }}
          </span>
          <span v-else>
            <i class="fas fa-arrow-down me-2"></i>
            {{ $t("Load More") }}
          </span>
        </button>
      </div>
    </div>

    <ReferenceModal
      v-if="showAddPaymentModal"
      :isVisible="showAddPaymentModal"
      :modelName="'payment'"
      :modelLabel="'Payment'"
      :lists="lists"
      :models="[]"
      :visualGroups="[]"
      :parentID="parentID"
      :selectedModel="parentModel"
      :extraParams="paymentExtraParams"
      @close="closeAddPaymentModal"
      @saved="onPaymentSaved"
    />

    <AccountStatementColumnFilterPopup
      :open="columnFilterPopupOpen"
      :field="columnFilterPopupField"
      :initial-filter="columnFilterPopupInitial"
      :list-options="columnFilterPopupListOptions"
      @close="closeStatementColumnFilter"
      @apply="applyStatementColumnFilter"
    />
  </div>
</template>

<script>
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import ExportService from "@/services/exportService";
import ReferenceModal from "@/components/gotrading/common/ReferenceModal.vue";
import AccountStatementColumnFilterPopup from "@/components/financial/AccountStatementColumnFilterPopup.vue";
import ColumnSelector from "@/components/gotrading/common/ColumnSelector.vue";
import columnPreferencesMixin, {
  DEFAULT_COLUMN_WIDTH_PX,
} from "@/components/gotrading/mixins/columnPreferencesMixin";
import permissionsMixin from "@/mixins/permissionsMixin";
import { formatNumberSync } from "@/utils/numberFormatter";
import { formatDateSync } from "@/utils/dateFormatter";
import {
  SCOPE_GENERAL_ACCOUNT_STATEMENT,
  fetchPerViewRowHideActive,
  bulkPerViewRowHide,
} from "@/modules/per_view_row_hide_module/perViewRowHideApi";
import {
  isAccountStatementDebitRow,
  isAccountStatementCreditRow,
  accountStatementDebitDisplayAmount,
} from "@/utils/accountStatementTxnColumns";
import {
  isFilterSpecActive,
  serializeActiveColumnFilters as serializeColumnFilterSpecs,
} from "@/utils/columnFilterSpec";
import MissingPrepaymentWarningIcon from "@/components/gotrading/common/MissingPrepaymentWarningIcon.vue";
import mobileLayoutMixin from "@/mixins/mobileLayoutMixin";
import MobileRecordCard from "@/components/mobile/MobileRecordCard.vue";
import MobileCardLine from "@/components/mobile/MobileCardLine.vue";
import MobileBottomSheet from "@/components/mobile/MobileBottomSheet.vue";
import MobileSheetItem from "@/components/mobile/MobileSheetItem.vue";
import MobileChips from "@/components/mobile/MobileChips.vue";
import MobileIconButton from "@/components/mobile/MobileIconButton.vue";
import BalanceDateRangeFilter from "@/components/financial/BalanceDateRangeFilter.vue";
import { hasMissingAssociatedPrepayment } from "@/utils/missingPrepaymentWarning";
import { isDuntowerTenant } from "@/utils/dynamicModelValidationSwal";

/** Gasto al proveedor en etapa proforma: fuera del saldo, pero se puede pedir su anticipo. */
const PROFORM_SUPPLIER_ROW_TYPE = "proform_supplier_expense";

/**
 * Catálogo de columnas del extracto. Hace de "campos del modelo" para el
 * ColumnSelector y el mixin de preferencias: las filas del extracto se
 * sintetizan en el backend, así que no hay ModelDefinition de donde sacarlas.
 *
 * `filterable: false` = el backend no sabe filtrar por esa columna
 * (ver accountStatementColumnFilters.getColumnCell).
 */
const STATEMENT_COLUMNS = [
  { key: "proform", label: "Proform", type: "string", filterable: true },
  { key: "shipping", label: "shipping", type: "string", filterable: true },
  { key: "shippingDate", label: "shippingDate", type: "date", filterable: true },
  { key: "etd", label: "etd", type: "date", filterable: true },
  { key: "eta", label: "eta", type: "date", filterable: true },
  { key: "transitDays", label: "Transit days", type: "number", filterable: true },
  { key: "date", label: "Date", type: "date", filterable: true },
  { key: "reference", label: "Reference", type: "string", filterable: true },
  { key: "comment", label: "Comment", type: "string", filterable: true },
  { key: "file", label: "File", type: "string", filterable: true },
  { key: "status", label: "Status", type: "list", filterable: true },
  { key: "type", label: "Type", type: "string", filterable: true },
  { key: "debit", label: "Debit", type: "number", filterable: true },
  { key: "credit", label: "Credit", type: "number", filterable: true },
  { key: "balance", label: "Balance", type: "number", filterable: true },
  {
    key: "accumulatedBalance",
    label: "Accumulated Balance",
    type: "number",
    filterable: false,
  },
];

/**
 * Columna que solo existe en el extracto general: ahí cada fila puede ser de
 * un contacto distinto, mientras que en el extracto de contacto es siempre el
 * mismo y la columna no aportaría nada.
 */
const CONTACT_STATEMENT_COLUMN = {
  key: "contact",
  label: "Contact",
  type: "string",
  filterable: true,
};

const GENERAL_STATEMENT_COLUMNS = [
  CONTACT_STATEMENT_COLUMN,
  ...STATEMENT_COLUMNS,
];

/** Clase de ancho por columna (definidas en el <style> de este componente). */
const STATEMENT_COL_CLASS = {
  contact: "as-col-contact",
  proform: "as-col-proform",
  shipping: "as-col-shipping",
  shippingDate: "as-col-shipping-date",
  etd: "as-col-etd",
  eta: "as-col-eta",
  transitDays: "as-col-transit",
  date: "as-col-date",
  reference: "as-col-reference",
  comment: "as-col-comment",
  file: "as-col-file",
  status: "as-col-status",
  type: "as-col-type",
  debit: "as-col-money",
  credit: "as-col-money",
  balance: "as-col-money",
  accumulatedBalance: "as-col-accumulated",
};

/**
 * Ancho real de cada columna, en px. Espejo de las reglas `.as-col-*` del
 * <style> de este componente: el mixin necesita el ancho para calcular el
 * desplazamiento de las columnas fijadas, y si no coincide con el que pinta
 * el CSS las columnas fijadas se solapan.
 */
const STATEMENT_COLUMN_WIDTH_PX = {
  contact: 170,
  proform: 155,
  shipping: 190,
  shippingDate: 105,
  etd: 105,
  eta: 105,
  transitDays: 90,
  date: 105,
  reference: 130,
  comment: 165,
  file: 115,
  status: 100,
  type: 130,
  debit: 115,
  credit: 115,
  balance: 115,
  accumulatedBalance: 140,
};

/** Clases de celda en filas de movimiento (el <tr> ya centra). */
const STATEMENT_TX_CELL_CLASS = {
  contact: "align-middle",
  proform: "align-middle",
  shipping: "align-middle",
  reference: "as-cell-reference",
  comment: "text-center",
  file: "text-center",
  status: "text-center",
  type: "text-center",
  debit: "text-center",
  credit: "text-center",
  balance: "text-center",
  accumulatedBalance: "text-center",
};

/** Clases de celda en filas de grupo (el <tr> no centra). */
const STATEMENT_GROUP_CELL_CLASS = {
  contact: "text-center align-middle",
  reference: "text-center align-middle as-cell-reference",
  debit: "text-center align-middle text-danger fw-bold",
  credit: "text-center align-middle text-success fw-bold",
};

export default {
  name: "AccountStatement",
  mixins: [permissionsMixin, columnPreferencesMixin, mobileLayoutMixin],
  components: {
    MobileRecordCard,
    MobileCardLine,
    MobileBottomSheet,
    MobileSheetItem,
    MobileChips,
    MobileIconButton,
    ContentSkeleton,
    ReferenceModal,
    AccountStatementColumnFilterPopup,
    ColumnSelector,
    MissingPrepaymentWarningIcon,
    BalanceDateRangeFilter,
  },
  props: {
    /**
     * Extracto de un contacto. Vacío = extracto general (todos los contactos):
     * misma tabla, mismas columnas + Contacto, mismos filtros.
     */
    contactId: {
      type: String,
      default: "",
    },
    /**
     * Extracto de un contacto dentro de la cuenta corriente general agrupada
     * por contacto: sin cabecera, tarjetas ni filtros propios; los toma de
     * `embeddedFilters`.
     */
    embedded: {
      type: Boolean,
      default: false,
    },
    /** `{ statementView, hiddenView, startDate, endDate, searchText, excludeBalanceZero }` */
    embeddedFilters: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      /** Teléfono: fila del extracto cuya hoja de acciones está abierta. */
      mobileStatementRow: null,
      mobileFiltersOpen: false,
      mobileToolsOpen: false,
      // Data
      transactions: [],
      // Gastos en etapa proforma: provisorios, fuera de los cálculos del extracto.
      proformSupplierTransactions: [],
      proformSupplierTotal: 0,
      contactName: "",
      initialBalance: 0,
      pagination: {},
      currentPage: 1,
      totals: {
        totalDebits: 0,
        totalCredits: 0,
        finalBalance: 0,
      },

      // Loading
      loading: false,
      loadingMore: false,
      exportingExcel: false,

      // Sorting
      sortBy: "date",
      sortOrder: "asc",
      /** Orden por columna elegido en la cabecera ("" = orden cronológico). */
      statementSortColumn: "",
      statementSortDirection: "asc",

      // Filtering
      excludeBalanceZero: true,
      searchText: "",
      startDate: "",
      endDate: "",
      /** Per-column filters (Proform → Balance), same UX as dynamic list headers */
      columnFilters: {},
      showColumnSelector: false,
      columnFilterPopupOpen: false,
      columnFilterPopupKey: "",

      // Lists for type labels
      lists: [],

      /** Filter rows by proforma / shipping (API query; clears the other axis). */
      filterProformId: "",
      filterShippingId: "",
      filterProformLabel: "",
      filterShippingLabel: "",

      /** Hover highlight: all rows sharing same proform or shipping id */
      hoverProformId: "",
      hoverShippingId: "",

      /** Groups are open by default; only explicitly closed keys are stored. */
      collapsedStatementGroups: {},

      /** active = movimientos; archived = archivadas; all = movimientos + archivadas */
      statementView: "active",

      showAddPaymentModal: false,
      parentID: null,
      parentModel: null,
      paymentExtraParams: {},

      // Debounce timer for search
      searchDebounceTimer: null,
      hiddenByExcludeBalanceZero: false,
      rowHideEnabled: false,
      hiddenView: false,
      rowHideBusy: false,
      /**
       * Única selección de filas del listado: sirve para ocultar/desocultar y
       * para el pedido de pago. Claves = línea (`_id`), igual que el listado general.
       */
      selectedRowKeys: [],
      /** Meta por clave (payable, saldo, shipping) de filas cargadas y de "seleccionar todo". */
      rowMetaByKey: {},
      selectingAllRows: false,
    };
  },
  created() {
    // Semilla sincrónica con el extracto completo: el watcher de contactId
    // corre antes de mounted, y sin esto la tabla se pintaría sin columnas
    // mientras llegan las preferencias.
    this.selectedColumns = this.statementColumns.reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {});
    this.visibleTableFields = [...this.statementColumns];
  },
  async mounted() {
    this.rowHideEnabled = await fetchPerViewRowHideActive();
    await this.initStatementColumnPreferences();
    await this.loadLists();
  },
  beforeDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  watch: {
    contactId: {
      immediate: true,
      handler(newContactId, oldContactId) {
        // El extracto general no tiene contacto: entra igual en la carga.
        if (newContactId === "undefined") return;
        if (newContactId && newContactId === oldContactId) return;
        if (!newContactId && oldContactId !== undefined) return;
        this.filterProformId = "";
        this.filterShippingId = "";
        this.filterProformLabel = "";
        this.filterShippingLabel = "";
        this.hoverProformId = "";
        this.hoverShippingId = "";
        this.collapsedStatementGroups = {};
        this.statementView = "active";
        this.hiddenView = false;
        this.columnFilters = {};
        this.closeStatementColumnFilter();
        this.currentPage = 1;
        this.clearRowSelection();
        if (this.embedded) this.applyEmbeddedFilters();
        this.loadAccountStatement();
      },
    },
    embeddedFilters: {
      deep: true,
      handler() {
        if (!this.embedded) return;
        this.applyEmbeddedFilters();
        this.filterProformId = "";
        this.filterShippingId = "";
        this.filterProformLabel = "";
        this.filterShippingLabel = "";
        this.clearHoverHighlight();
        this.currentPage = 1;
        this.clearRowSelection();
        this.loadAccountStatement();
      },
    },
  },
  computed: {
    // Las mismas pestañas que la barra de escritorio, como chips del teléfono.
    mobileStatementTabs() {
      const tabs = [
        { key: "active", label: this.$t("accountStatementTabMovements") },
        { key: "archived", label: this.$t("accountStatementTabArchived") },
      ];
      if (this.rowHideEnabled) tabs.push({ key: "hidden", label: this.$t("listTitleHidden") });
      tabs.push({ key: "all", label: this.$t("accountStatementTabAll") });
      return tabs.map((tab) => ({ ...tab, active: this.statementTab === tab.key }));
    },
    /**
     * Seam del mixin: las preferencias del extracto se guardan bajo una clave
     * propia (viewId en UserColumnPreference es texto libre, no un ObjectId).
     */
    columnPreferencesViewId() {
      return this.isGeneralStatement
        ? "general_account_statement"
        : "account_statement";
    },
    /** Sin contacto el extracto es el general (todos los contactos). */
    isGeneralStatement() {
      return !this.contactId;
    },
    /**
     * Filas por página. El extracto general recorre todos los contactos y con
     * 50 por página tardaba demasiado en pintar la primera pantalla.
     */
    statementPageSize() {
      return this.isGeneralStatement ? 20 : 50;
    },
    statementTotalCount() {
      return Number(this.pagination && this.pagination.totalCount) || 0;
    },
    statementTotalPages() {
      return Number(this.pagination && this.pagination.totalPages) || 0;
    },
    statementRangeFrom() {
      if (!this.statementTotalCount) return 0;
      return (this.currentPage - 1) * this.statementPageSize + 1;
    },
    statementRangeTo() {
      return Math.min(
        this.currentPage * this.statementPageSize,
        this.statementTotalCount
      );
    },
    /** Ventana de páginas alrededor de la actual (el total puede ser enorme). */
    statementPageWindow() {
      const totalPages = this.statementTotalPages;
      if (totalPages <= 1) return [];
      const maxButtons = 7;
      const half = Math.floor(maxButtons / 2);
      let first = Math.max(1, this.currentPage - half);
      const last = Math.min(totalPages, first + maxButtons - 1);
      first = Math.max(1, last - maxButtons + 1);
      const pages = [];
      for (let page = first; page <= last; page += 1) pages.push(page);
      return pages;
    },
    /** Endpoint del extracto; el general es la misma ruta sin contacto. */
    statementEndpoint() {
      return this.isGeneralStatement
        ? "/api/financial/account-statement"
        : `/api/financial/account-statement/${this.contactId}`;
    },
    /** Catálogo según el alcance: el general agrega la columna Contacto. */
    statementColumns() {
      return this.isGeneralStatement
        ? GENERAL_STATEMENT_COLUMNS
        : STATEMENT_COLUMNS;
    },
    statementFilterableColumns() {
      return this.statementColumns.filter((col) => col.filterable);
    },
    /**
     * Columnas ordenables. Espejo de `SORTABLE_COLUMNS` en
     * `back/modules/financial_module/utils/accountStatementColumnFilters.js`.
     */
    statementSortableColumns() {
      return this.statementColumns.map((col) => col.key);
    },
    /**
     * Tab activo. `hiddenView` es un flag aparte de `statementView`, así que
     * se proyectan los dos en una sola selección para la barra de tabs.
     */
    statementTab() {
      if (this.rowHideEnabled && this.hiddenView) return "hidden";
      return this.statementView;
    },
    /** Catálogo completo; hace de `tableFields` para el ColumnSelector. */
    statementColumnCatalog() {
      return this.statementColumns;
    },
    /**
     * Columnas del listado de gastos en etapa proforma: las mismas del extracto
     * menos las contables (débito, crédito y saldos), que ahí no aplican —
     * el listado lleva una única columna de importe.
     */
    proformSupplierColumns() {
      const excluded = new Set([
        "debit",
        "credit",
        "balance",
        "accumulatedBalance",
      ]);
      return this.displayTableColumns.filter((col) => !excluded.has(col.key));
    },
    /** ¿Hay columnas ocultas respecto al catálogo completo? */
    hiddenStatementColumnCount() {
      return this.statementColumns.length - this.displayTableColumns.length;
    },
    columnFilterPopupField() {
      if (!this.columnFilterPopupKey) return null;
      const col = this.statementFilterableColumns.find(
        (c) => c.key === this.columnFilterPopupKey
      );
      if (!col) return null;
      return {
        key: col.key,
        label: col.label,
        type: col.type,
      };
    },
    columnFilterPopupInitial() {
      if (!this.columnFilterPopupKey) return null;
      return this.columnFilters[this.columnFilterPopupKey] || null;
    },
    columnFilterPopupListOptions() {
      if (this.columnFilterPopupKey !== "status") return [];
      return this.statementStatusFilterOptions;
    },
    statementStatusFilterOptions() {
      return [
        { _id: "pending", label: this.$t("Pending") },
        { _id: "completed", label: this.$t("Received") },
        { _id: "paid", label: this.$t("Paid") },
        { _id: "cancelled", label: this.$t("Cancelled") },
        { _id: "failed", label: this.$t("Failed") },
        { _id: "emitted", label: this.$t("Emitted") },
      ];
    },
    /** Filtros activos, para mostrarlos y poder quitarlos de uno en uno. */
    activeStatementFilterChips() {
      return Object.keys(this.columnFilters || {})
        .filter((key) => this.isStatementColumnFilterActive(key))
        .map((key) => {
          const col = this.statementFilterableColumns.find((c) => c.key === key);
          return {
            key,
            label: col ? col.label : key,
            text: this.statementFilterChipText(key),
          };
        });
    },
    hasActiveColumnFilters() {
      return !!this.serializeActiveColumnFilters();
    },
    isStatementSorted() {
      return !!this.statementSortColumn;
    },
    /**
     * Con orden por columna o filtros activos la tabla va plana (ver
     * `groupedTransactionsRows` y `flattenGroups`). El Excel tiene que hacer lo
     * mismo: si reagrupa por proforma/shipping sale en un orden que en pantalla
     * no existe.
     */
    isStatementFlatExport() {
      return this.isStatementSorted || this.hasActiveColumnFilters;
    },
    /**
     * Columnas del Excel: las visibles de la tabla, en su mismo orden, para que
     * lo que se baja sea lo que se ve. La única que no sale del selector es el
     * balance por shipping (ticket #314), que en pantalla vive en la fila de
     * resumen del grupo y no como columna; va pegada a Balance, o al final si
     * esa columna está oculta.
     */
    statementExportColumns() {
      const columns = this.displayTableColumns
        .filter((col) => col.displayType !== "merged")
        .map((col) => ({
          key: col.key,
          label: this.$t(this.statementColumnLabel(col)),
        }));
      if (this.isStatementFlatExport) return columns;
      const shippingBalanceColumn = {
        key: "shippingBalance",
        label: `${this.$t("Balance")} (${this.$t("shipping")})`,
      };
      const balanceIndex = columns.findIndex((col) => col.key === "balance");
      if (balanceIndex < 0) {
        columns.push(shippingBalanceColumn);
      } else {
        columns.splice(balanceIndex + 1, 0, shippingBalanceColumn);
      }
      return columns;
    },
    canMakePayments() {
      // El pedido de pago se arma contra un contacto; en el general las filas
      // pueden ser de contactos distintos.
      if (this.isGeneralStatement) return false;
      if (this.hasPermission("finance.manage")) {
        return true;
      }
      return this.hasPermission("finance.payments");
    },
    showPayableSelection() {
      return this.canMakePayments && this.statementView === "active";
    },
    /** Una sola columna de checkbox para ocultar y para pedir pago. */
    showRowSelectionColumn() {
      return this.rowHideEnabled || this.showPayableSelection;
    },
    showStatementActionsColumn() {
      return (
        this.statementView === "archived" ||
        this.statementView === "all" ||
        this.rowHideEnabled
      );
    },
    /**
     * Claves marcables. Con ocultar activo, cualquier línea; si solo está
     * disponible el pedido de pago, únicamente gastos con saldo pendiente.
     */
    selectableRowKeys() {
      return this.selectableKeysFrom(this.transactions);
    },
    selectableRowKeySet() {
      return new Set(this.selectableRowKeys);
    },
    isAllRowsSelected() {
      const keys = this.selectableRowKeys;
      if (!keys.length || !this.selectedRowKeys.length) return false;
      const selected = new Set(this.selectedRowKeys.map(String));
      return keys.every((key) => selected.has(String(key)));
    },
    isRowSelectionHeaderIndeterminate() {
      if (!this.selectedRowKeys.length || this.isAllRowsSelected) return false;
      const selected = new Set(this.selectedRowKeys.map(String));
      return this.selectableRowKeys.some((key) => selected.has(String(key)));
    },
    /** Claves de la lista de proformas del proveedor: se marcan solo para pedir pago. */
    proformSupplierRowKeySet() {
      return new Set(
        (this.proformSupplierTransactions || [])
          .map((row) => this.rowLineKey(row))
          .filter(Boolean)
      );
    },
    /** Lo marcado que se puede ocultar: todo menos las proformas del proveedor. */
    hideableSelectedRowKeys() {
      const proformSupplierKeys = this.proformSupplierRowKeySet;
      return (this.selectedRowKeys || []).filter(
        (key) => !proformSupplierKeys.has(String(key))
      );
    },
    selectedPayableIds() {
      return [...new Set((this.selectedRowKeys || []).map(String))].filter(
        (key) => this.rowMetaByKey[key] && this.rowMetaByKey[key].payable
      );
    },
    selectedPayableAmount() {
      return this.selectedPayableIds.reduce((sum, key) => {
        const amount = Number(this.rowMetaByKey[key].unpaid);
        return sum + (Number.isFinite(amount) ? amount : 0);
      }, 0);
    },
    /** Currency from first payment/collect linked to this contact; fallback USD */
    displayCurrency() {
      const first = (this.transactions || []).find(
        t => t && (t.type === 'payment' || t.type === 'collect') && t.currency
      );
      return (first && first.currency) || 'USD';
    },
    statementCollapsibleGroupKeys() {
      const proformCounts = new Map();
      const shippingCounts = new Map();
      const shippingGroupsPerProform = new Map();

      (this.transactions || []).forEach((transaction) => {
        const proformId = this.normalizeEntityId(
          this.getProformId(transaction)
        );
        const shippingId =
          this.normalizeEntityId(this.getShippingId(transaction)) ||
          (transaction.type === "shipping_pending" && transaction._id
            ? String(transaction._id)
            : "");
        const proformKey = this.statementProformGroupKey(
          transaction,
          proformId,
          shippingId
        );
        const shippingKey = shippingId
          ? `${proformKey}-shipping-${shippingId}`
          : `${proformKey}-without-shipping`;

        proformCounts.set(proformKey, (proformCounts.get(proformKey) || 0) + 1);
        shippingCounts.set(
          shippingKey,
          (shippingCounts.get(shippingKey) || 0) + 1
        );
        if (!shippingGroupsPerProform.has(proformKey)) {
          shippingGroupsPerProform.set(proformKey, new Set());
        }
        shippingGroupsPerProform.get(proformKey).add(shippingKey);
      });

      const shippingKeysForMultiShipProforms = [];
      shippingGroupsPerProform.forEach((set, proformKey) => {
        if (set.size > 1) {
          set.forEach((shippingKey) => {
            shippingKeysForMultiShipProforms.push(`statement-${shippingKey}`);
          });
        }
      });

      return [
        ...Array.from(proformCounts.entries())
          .filter(([, count]) => count > 1)
          .map(([key]) => `statement-${key}`),
        ...Array.from(
          new Set([
            ...Array.from(shippingCounts.entries())
              .filter(([, count]) => count > 1)
              .map(([key]) => `statement-${key}`),
            ...shippingKeysForMultiShipProforms,
          ])
        ),
      ];
    },
    hasCollapsibleStatementGroups() {
      if (this.hasActiveColumnFilters || this.isStatementSorted) return false;
      return this.statementCollapsibleGroupKeys.length > 0;
    },
    hasCollapsedStatementGroups() {
      return this.statementCollapsibleGroupKeys.some((key) =>
        this.isStatementGroupCollapsed(key)
      );
    },
    groupedTransactionsRows() {
      // runningBalance viene del backend e incluye ya initial_balance del contacto.
      // Accumulated Balance reuses that running total on each line (backend never
      // sends accumulatedBalance on leaf rows — only group summaries used to set it).
      const transactions = (this.transactions || []).map((transaction) => {
        const runningBalance = Number(transaction.runningBalance) || 0;
        const next = {
          type: "transaction",
          ...transaction,
          runningBalance,
        };
        if (
          !transaction.excludedFromTotals &&
          transaction.type !== "pending_prepayment"
        ) {
          next.accumulatedBalance = runningBalance;
        }
        return next;
      });

      // Con orden por columna activo el extracto va plano y en el orden que
      // mandó el backend (que ordenó antes de paginar). Reagrupar por proforma
      // y shipping acá volvería a reordenar las filas y pisaría ese orden.
      if (this.isStatementSorted) {
        return this.withStatementInitialBalanceRow(
          transactions.map((transaction) => ({ ...transaction }))
        );
      }

      const proformGroups = [];
      const proformGroupsByKey = new Map();

      transactions.forEach((transaction) => {
        const proformId = this.normalizeEntityId(this.getProformId(transaction));
        const shippingId =
          this.normalizeEntityId(this.getShippingId(transaction)) ||
          (transaction.type === "shipping_pending" && transaction._id
            ? String(transaction._id)
            : "");
        const proformKey = this.statementProformGroupKey(
          transaction,
          proformId,
          shippingId
        );
        const shippingKey = shippingId
          ? `${proformKey}-shipping-${shippingId}`
          : `${proformKey}-without-shipping`;

        let proformGroup = proformGroupsByKey.get(proformKey);
        if (!proformGroup) {
          proformGroup = {
            key: proformKey,
            entityId: proformId,
            label: proformId ? this.getProformDisplay(transaction) : "-",
            transactionCount: 0,
            shippingGroups: [],
            shippingGroupsByKey: new Map(),
          };
          proformGroupsByKey.set(proformKey, proformGroup);
          proformGroups.push(proformGroup);
        }

        let shippingGroup = proformGroup.shippingGroupsByKey.get(shippingKey);
        if (!shippingGroup) {
          shippingGroup = {
            key: shippingKey,
            entityId: shippingId,
            label: shippingId ? this.getShippingDisplay(transaction) : "-",
            transactions: [],
          };
          proformGroup.shippingGroupsByKey.set(shippingKey, shippingGroup);
          proformGroup.shippingGroups.push(shippingGroup);
        }

        proformGroup.transactionCount += 1;
        shippingGroup.transactions.push(transaction);
      });

      // Column filters match transaction cells; group headers show summed Credit/Debit
      // (e.g. 1743+1743=3486) and look like the filter failed. Flatten while filters are on.
      // Con filtros activos las cabeceras de grupo suman Credit/Debit de filas
      // que el filtro escondió y parece que el filtro falló: se aplana.
      // (El caso "hay orden por columna" ya salió antes, plano.)
      const flattenGroups = this.hasActiveColumnFilters;

      const rows = [];
      proformGroups.forEach((proformGroup) => {
        proformGroup.shippingGroups.sort((a, b) =>
          this.compareShippingNamesForStatement(a.label, b.label)
        );
        const proformGroupKey = `statement-${proformGroup.key}`;
        const isProformCollapsible = proformGroup.transactionCount > 1;
        const isProformCollapsed =
          !flattenGroups &&
          isProformCollapsible &&
          this.isStatementGroupCollapsed(proformGroupKey);

        // Header with operation subtotals stays visible when expanded or collapsed.
        if (!flattenGroups && isProformCollapsible) {
          const proformTransactions = proformGroup.shippingGroups.flatMap(
            (shippingGroup) => shippingGroup.transactions
          );
          rows.push({
            type: "proform_group",
            groupKey: proformGroupKey,
            entityId: proformGroup.entityId,
            proform: proformGroup.entityId,
            proformName: proformGroup.label,
            proformAdvance: this.statementRowsAdvance(
              proformTransactions,
              "proformAdvance"
            ),
            label: proformGroup.label,
            transactionCount: proformGroup.transactionCount,
            lineKeys: this.lineKeysOf(proformTransactions),
            hiddenInView: proformTransactions.every((t) => t.hiddenInView),
            summary: this.buildStatementGroupSummary(proformTransactions),
          });
          if (isProformCollapsed) {
            return;
          }
        }

        proformGroup.shippingGroups.forEach((shippingGroup) => {
          shippingGroup.transactions = this.sortShippingTransactionsForStatement(
            shippingGroup.transactions
          );
          const shippingGroupKey = `statement-${shippingGroup.key}`;
          // Siempre header por shipping si la PI tiene más de un shipping (aunque cada uno tenga 1 movimiento).
          const isShippingCollapsible =
            shippingGroup.transactions.length > 1 ||
            proformGroup.shippingGroups.length > 1;
          const isShippingCollapsed =
            !flattenGroups &&
            isShippingCollapsible &&
            this.isStatementGroupCollapsed(shippingGroupKey);

          if (!flattenGroups && isShippingCollapsible) {
            rows.push({
              type: "shipping_group",
              groupKey: shippingGroupKey,
              entityId: shippingGroup.entityId,
              proform: proformGroup.entityId,
              proformName: proformGroup.label,
              proformAdvance: this.statementRowsAdvance(
                shippingGroup.transactions,
                "proformAdvance"
              ),
              shipping: shippingGroup.entityId,
              shippingName: shippingGroup.label,
              shippingAdvance: this.statementRowsAdvance(
                shippingGroup.transactions,
                "shippingAdvance"
              ),
              label: shippingGroup.label,
              transactionCount: shippingGroup.transactions.length,
              lineKeys: this.lineKeysOf(shippingGroup.transactions),
              hiddenInView: shippingGroup.transactions.every(
                (t) => t.hiddenInView
              ),
              summary: this.buildStatementGroupSummary(
                shippingGroup.transactions
              ),
            });
            if (isShippingCollapsed) {
              return;
            }
          }

          shippingGroup.transactions.forEach((transaction) => {
            rows.push({ ...transaction });
          });
        });
      });

      return this.withStatementInitialBalanceRow(rows);
    }
  },
  methods: {
    // Cierra la hoja y corre la acción con esa fila: las mismas funciones que
    // los botones de la tabla de escritorio.
    mobileStatementDo(action) {
      const row = this.mobileStatementRow;
      this.mobileStatementRow = null;
      if (row) action(row);
    },
    /** Copia los filtros de la página que contiene al extracto embebido. */
    applyEmbeddedFilters() {
      const filters = this.embeddedFilters || {};
      this.statementView = filters.statementView || "active";
      this.hiddenView = Boolean(filters.hiddenView);
      this.startDate = filters.startDate || "";
      this.endDate = filters.endDate || "";
      this.searchText = filters.searchText || "";
      this.excludeBalanceZero = filters.excludeBalanceZero !== false;
    },

    /**
     * Clave del grupo de proforma de una fila. Sin proforma ni shipping todas
     * caerían en el mismo grupo, y en el extracto general eso mezclaría
     * contactos distintos: ahí la clave lleva además el contacto.
     */
    statementProformGroupKey(transaction, proformId, shippingId) {
      if (proformId) return `proform-${proformId}`;
      if (shippingId) return `shipping-only-${shippingId}`;
      if (!this.isGeneralStatement) return "without-proform";
      const contactKey =
        this.normalizeEntityId(transaction && transaction.contactId) || "none";
      return `without-proform-${contactKey}`;
    },

    /** Agrega la fila de saldo inicial al final, si corresponde mostrarla. */
    withStatementInitialBalanceRow(rows) {
      const showInitialRow =
        this.statementView === "active" &&
        !this.hiddenView &&
        !this.filterProformId &&
        !this.filterShippingId &&
        this.initialBalance !== 0 &&
        this.initialBalance !== null &&
        this.initialBalance !== undefined;

      if (showInitialRow) {
        rows.push({
          type: "initial_balance",
          _id: "initial-balance",
          name: this.$t("Initial Balance"),
          accumulatedBalance: this.initialBalance,
          totals: {
            balance: this.initialBalance,
          },
          level: 0,
        });
      }

      return rows;
    },

    // ----------------------------------------------------------- selección






    // -------------------------------------------------------------- filtros

    /** Texto legible del filtro para el chip. */
    statementFilterChipText(columnKey) {
      const spec = (this.columnFilters || {})[columnKey] || {};
      const operator = spec.operator || "=";
      if (operator === "is_null") return this.$t("Is empty");
      if (operator === "is_not_null") return this.$t("Is not empty");
      if (operator === "between") return `${spec.start} → ${spec.end}`;

      const raw =
        spec.value !== null && spec.value !== undefined && spec.value !== ""
          ? spec.value
          : spec.start || spec.end || "";
      const text = Array.isArray(raw)
        ? raw.map((item) => this.statementFilterValueLabel(columnKey, item)).join(", ")
        : this.statementFilterValueLabel(columnKey, raw);
      return operator === "=" ? text : `${operator} ${text}`;
    },

    statementFilterValueLabel(columnKey, value) {
      const id =
        value && typeof value === "object" ? value._id || value.label : value;
      if (columnKey === "status") {
        const option = this.statementStatusFilterOptions.find(
          (opt) => opt._id === id
        );
        if (option) return option.label;
      }
      return id == null ? "" : String(id);
    },

    async clearStatementColumnFilter(columnKey) {
      if (!Object.prototype.hasOwnProperty.call(this.columnFilters, columnKey)) {
        return;
      }
      this.$delete(this.columnFilters, columnKey);
      this.currentPage = 1;
      this.clearHoverHighlight();
      this.clearRowSelection();
      await this.loadAccountStatement();
    },

    async clearAllStatementColumnFilters() {
      this.columnFilters = {};
      this.currentPage = 1;
      this.clearHoverHighlight();
      this.clearRowSelection();
      await this.loadAccountStatement();
    },

    // ------------------------------------------------------------- columnas

    /** Seam del mixin: el reorden se guarda solo, igual que las fijadas. */
    onColumnOrderChanged() {
      this.saveStatementColumnPreferences();
    },

    /** Seam del mixin: el ancho también se guarda solo al soltar. */
    onColumnWidthChanged() {
      this.saveStatementColumnPreferences();
    },

    /** Seam del mixin: el ancho lo fija el CSS, no una preferencia guardada. */
    getColumnWidth(fieldKey) {
      return (
        this.columnWidths[fieldKey] ||
        STATEMENT_COLUMN_WIDTH_PX[fieldKey] ||
        DEFAULT_COLUMN_WIDTH_PX
      );
    },

    statementColClass(colKey) {
      return STATEMENT_COL_CLASS[colKey] || "";
    },

    statementColumnLabel(col) {
      return (col && col.field && col.field.label) || (col && col.key) || "";
    },

    isStatementColumnFilterable(col) {
      return !!(col && col.field && col.field.filterable);
    },

    /** Clases de columna fijada, comunes a las tres variantes de fila. */
    statementPinnedCellClass(col, colIndex) {
      if (!this.isColumnPinned(col.key)) return "";
      return this.isDataColPinnedBoundary(col, colIndex)
        ? "list-col-pinned list-col-pinned-boundary"
        : "list-col-pinned";
    },

    statementPlainCellClass(col, colIndex) {
      return this.statementPinnedCellClass(col, colIndex);
    },

    statementCellClass(col, colIndex) {
      return [
        STATEMENT_TX_CELL_CLASS[col.key] || "",
        this.statementPinnedCellClass(col, colIndex),
      ];
    },

    statementGroupCellClass(col, colIndex, rowType) {
      let base = STATEMENT_GROUP_CELL_CLASS[col.key];
      if (base === undefined) {
        // La columna que lleva el título del grupo no se centra.
        const isTitleColumn =
          (rowType === "proform_group" && col.key === "proform") ||
          (rowType === "shipping_group" && col.key === "shipping");
        base = isTitleColumn ? "align-middle" : "text-center align-middle";
      }
      return [base, this.statementPinnedCellClass(col, colIndex)];
    },

    statementSignedClass(amount) {
      return amount >= 0 ? "text-success fw-bold" : "text-danger fw-bold";
    },

    hasStatementAccumulatedBalance(row) {
      return (
        row.accumulatedBalance !== null && row.accumulatedBalance !== undefined
      );
    },

    /** El resaltado por hover solo aplica a las columnas de proforma y shipping. */
    onStatementCellEnter(colKey, row) {
      if (colKey === "proform") this.onHoverProformCell(row);
      else if (colKey === "shipping") this.onHoverShippingCell(row);
    },

    /** Orden guardado primero; lo nuevo del catálogo se agrega al final. */
    buildStatementVisibleFields() {
      const prefs = this.selectedColumns || {};
      const byKey = new Map(this.statementColumns.map((col) => [col.key, col]));
      const ordered = [];
      const used = new Set();
      (this.savedColumnOrder || []).forEach((key) => {
        if (used.has(key) || !byKey.has(key) || !prefs[key]) return;
        ordered.push(byKey.get(key));
        used.add(key);
      });
      this.statementColumns.forEach((col) => {
        if (!used.has(col.key) && prefs[col.key]) ordered.push(col);
      });
      return ordered;
    },

    async initStatementColumnPreferences() {
      const preferences = await this.loadColumnPreferences();
      const merged = { ...preferences };
      // Sin preferencia guardada la columna se ve: un usuario nuevo ve el
      // extracto completo, igual que antes de que esto fuera configurable.
      this.statementColumns.forEach((col) => {
        if (merged[col.key] === undefined) merged[col.key] = true;
      });
      this.selectedColumns = merged;
      this.visibleTableFields = this.buildStatementVisibleFields();
    },

    /** El ColumnSelector mutó `selectedColumns`; se reproyecta el orden. */
    handleStatementColumnsChanged() {
      const previousOrder = this.displayTableColumns.map((col) => col.key);
      this.savedColumnOrder = previousOrder.concat(
        (this.savedColumnOrder || []).filter(
          (key) => !previousOrder.includes(key)
        )
      );
      this.visibleTableFields = this.buildStatementVisibleFields();
      this.saveStatementColumnPreferences();
    },

    async saveStatementColumnPreferences() {
      try {
        await this.saveColumnPreferences(true, false);
        this.savedSelectedColumns = JSON.parse(
          JSON.stringify(this.selectedColumns)
        );
        this.savedPinnedColumnKeys = [...(this.pinnedColumnKeys || [])];
      } catch (error) {
        console.error("Error saving statement column preferences:", error);
      }
    },

    // Navigation methods
    goToDashboard() {
      this.$router.push("/financial");
    },

    /** API may return file as string, string[], or { url|path|name }. */
    statementFileRefs(file) {
      if (file == null || file === "") return [];
      const toRef = (entry) => {
        if (entry == null || entry === "") return "";
        if (typeof entry === "string") return entry.trim();
        if (typeof entry === "object") {
          const ref = entry.url || entry.path || entry.name || "";
          return typeof ref === "string" ? ref.trim() : "";
        }
        return "";
      };
      if (Array.isArray(file)) {
        return file.map(toRef).filter(Boolean);
      }
      const single = toRef(file);
      return single ? [single] : [];
    },

    statementFileHref(file) {
      return this.statementFileRefs(file)[0] || "";
    },

    statementFileTail(file) {
      const ref = this.statementFileHref(file);
      if (!ref) return "";
      return ref.length > 10 ? ref.substring(ref.length - 10) : ref;
    },

    /** Salta a una página del extracto general (no acumula, reemplaza). */
    goToStatementPage(page) {
      const target = Number(page);
      if (!Number.isFinite(target) || target < 1) return;
      if (this.statementTotalPages && target > this.statementTotalPages) return;
      if (this.loading || target === this.currentPage) return;
      this.currentPage = target;
      this.loadAccountStatement(false, { keepPage: true });
      if (this.$el && this.$el.scrollIntoView) {
        this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },

    async loadAccountStatement(append = false, { keepPage = false } = {}) {
      try {
        if (append) {
          this.loadingMore = true;
        } else {
          this.loading = true;
          // Al cambiar de página la página elegida no se pisa.
          if (!keepPage) this.currentPage = 1;
          this.selectedRowKeys = [];
          this.rowMetaByKey = {};
        }

        const params = this.buildQueryParams();
        const response = await wencesApi.get(this.statementEndpoint, { params });

        if (response.data.success) {
          const newTransactions = response.data.data.transactions || [];
          
          if (append) {
            // Append new transactions to existing ones
            this.transactions = [...this.transactions, ...newTransactions];
          } else {
            // Replace transactions (initial load or reset)
            this.transactions = newTransactions;
            // Por defecto: PIs colapsadas para abrir shipping por shipping (menos mezcla).
            this.$nextTick(() => {
              const collapsed = {};
              (this.statementCollapsibleGroupKeys || []).forEach((key) => {
                if (String(key).includes("-shipping-") || String(key).includes("-without-shipping")) {
                  return;
                }
                collapsed[key] = true;
              });
              this.collapsedStatementGroups = collapsed;
            });
          }

          this.mergeRowMeta(append ? newTransactions : this.transactions);

          this.contactName = this.isGeneralStatement
            ? ""
            : response.data.data.contactName || this.$t("Contact");
          this.initialBalance = response.data.data.initialBalance || 0;
          this.pagination = response.data.data.pagination || {};
          this.totals = response.data.data.totals || {
            totalDebits: 0,
            totalCredits: 0,
            finalBalance: 0,
          };
          this.proformSupplierTransactions =
            response.data.data.proformSupplierTransactions || [];
          this.mergeRowMeta(this.proformSupplierTransactions);
          this.proformSupplierTotal =
            Number(response.data.data.proformSupplierTotal) || 0;
          this.hiddenByExcludeBalanceZero = Boolean(
            response.data.data.hiddenByExcludeBalanceZero
          );
          if (!append) {
            // La página general actualiza la fila del contacto (p. ej. después
            // de archivar u ocultar acá). Con filtro de proforma, shipping o
            // por columna los totales son de un recorte y no del contacto.
            this.$emit("statement-loaded", {
              totals: this.totals,
              totalCount: Number(this.pagination.totalCount) || 0,
              complete:
                !this.filterProformId &&
                !this.filterShippingId &&
                !this.hasActiveColumnFilters,
            });
          }
        } else {
          throw new Error(
            response.data.error || "Failed to load account statement"
          );
        }
      } catch (error) {
        console.error("Error loading account statement:", error);

        // Only show error if it's not a "not found" or empty result case
        if (error.response?.status !== 404 && error.response?.status !== 200) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Error loading account statement",
          });
        } else {
          // Handle empty result gracefully
          if (!append) {
            this.transactions = [];
            this.contactName = this.isGeneralStatement
              ? ""
              : this.$t("Contact");
            this.pagination = {
              currentPage: 1,
              totalPages: 0,
              totalItems: 0,
              itemsPerPage: 50,
            };
            this.totals = {
              totalDebits: 0,
              totalCredits: 0,
              finalBalance: 0,
            };
            this.proformSupplierTransactions = [];
            this.proformSupplierTotal = 0;
            this.hiddenByExcludeBalanceZero = false;
          }
        }
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    async loadMore() {
      if (this.pagination.hasNextPage && !this.loadingMore) {
        this.currentPage += 1;
        await this.loadAccountStatement(true);
      }
    },

    buildQueryParams({ page, limit } = {}) {
      const params = {
        page: page != null ? page : this.currentPage,
        limit: limit != null ? limit : this.statementPageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        excludeBalanceZero: this.excludeBalanceZero,
        endDate: this.endDate || undefined,
      };

      // El backend ordena antes de paginar, así el orden vale sobre todo el
      // extracto y no solo sobre las filas ya cargadas.
      if (this.statementSortColumn) {
        params.sortColumn = this.statementSortColumn;
        params.sortDirection = this.statementSortDirection;
      }
      if (this.startDate) {
        params.startDate = this.startDate;
      }
      
      // Add searchText if provided
      if (this.searchText && this.searchText.trim() !== '') {
        params.searchText = this.searchText.trim();
      }

      if (this.filterProformId) {
        params.proformId = this.filterProformId;
      }
      if (this.filterShippingId) {
        params.shippingId = this.filterShippingId;
      }

      const activeColumnFilters = this.serializeActiveColumnFilters();
      if (activeColumnFilters) {
        params.columnFilters = JSON.stringify(activeColumnFilters);
      }

      if (this.statementView === "archived") {
        params.view = "archived";
      } else if (this.statementView === "all") {
        params.view = "all";
      }
      // Embebido, `rowHideEnabled` llega después de la primera carga; el tab
      // Ocultas solo existe si el módulo está activo, así que alcanza con hiddenView.
      if ((this.rowHideEnabled || this.embedded) && this.hiddenView) {
        params.hidden = true;
      }

      return params;
    },

    buildExportQueryParams() {
      return this.buildQueryParams({ page: 1, limit: 100000 });
    },

    getStatementTypeLabel(row) {
      if (!row || row.type === "initial_balance") return "";
      if (row.unapplied) return this.$t("Unassigned");
      if (row.type === "expense") {
        return this.getTypeLabel(row.expenseType);
      }
      // Pago de un gasto al proveedor: PAYMENT PS (la proforma) o PAYMENT IS (la factura).
      if (row.type === "payment" && row.supplierStage) {
        return `${this.getTransactionTypeLabel(row.type)} ${row.supplierStage}`;
      }
      return this.getTransactionTypeLabel(row.type);
    },

    /** Type column: payment.type, collect.type or shipping_expenses.type */
    getPaymentExpenseTypeLabel(row) {
      if (!row) return "";
      if (row.unapplied) return this.$t("Unassigned");
      if (
        row.type !== "payment" &&
        row.type !== "collect" &&
        row.type !== "expense"
      ) {
        return "";
      }
      const raw = row.expenseType;
      if (raw == null || raw === "") return "";
      const label = this.getTypeLabel(raw);
      return label && label !== "-" ? label : String(raw);
    },

    emptyExportCell(value) {
      if (value == null) return "";
      const text = String(value).trim();
      if (!text || text === "-" || text === "—") return "";
      return text;
    },

    formatShippingCellForExport(row) {
      if (!row || row.type === "initial_balance") {
        return row?.name || this.$t("Initial Balance");
      }
      const shipping = this.emptyExportCell(this.getShippingDisplay(row));
      const typeLabel = this.getStatementTypeLabel(row);
      if (!shipping && typeLabel) return typeLabel;
      if (typeLabel && shipping) {
        return `${shipping} (${typeLabel})`;
      }
      return shipping;
    },

    formatDebitCellForExport(row) {
      if (this.isStatementDebitRow(row)) {
        return this.formatStatementDebit(this.statementDebitDisplayAmount(row));
      }
      if (row.type === "initial_balance" && row.totals?.balance < 0) {
        return this.formatStatementDebit(row.totals.balance);
      }
      return "";
    },

    statementExportTypeFontColor(row) {
      if (!row) return null;
      if (row.type === "shipping_pending") return "FF0070C0";
      if (row.type === "collect" || row.type === "invoice_shipper") {
        return "FF548235";
      }
      if (row.type === "pending_prepayment") return "FFC00000";
      return null;
    },

    statementExportShippingBalanceFontColor(balance) {
      const n = Number(balance);
      if (!Number.isFinite(n)) return null;
      if (Math.abs(n) < 0.0001) return "FF548235";
      return "FFC00000";
    },

    formatCreditCellForExport(row) {
      if (this.isStatementCreditRow(row)) {
        return this.formatStatementCredit(row.amount);
      }
      if (row.type === "initial_balance" && row.totals?.balance >= 0) {
        return this.formatStatementCredit(row.totals.balance);
      }
      return "";
    },

    formatBalanceCellForExport(row) {
      if (row.type === "initial_balance") {
        return this.formatStatementSigned(row.totals?.balance || 0);
      }
      if (row.excludedFromTotals || row.type === "pending_prepayment") {
        return "";
      }
      return this.formatStatementSigned(row.runningBalance);
    },

    formatAccumulatedBalanceCellForExport(row) {
      if (row && row.type === "initial_balance") {
        if (
          row.accumulatedBalance !== null &&
          row.accumulatedBalance !== undefined
        ) {
          return this.formatNumber(row.accumulatedBalance);
        }
        return this.formatNumber(row.totals?.balance || 0);
      }
      if (
        !row ||
        row.excludedFromTotals ||
        row.type === "pending_prepayment"
      ) {
        return "";
      }
      if (
        row.accumulatedBalance !== null &&
        row.accumulatedBalance !== undefined
      ) {
        return this.formatStatementSigned(row.accumulatedBalance);
      }
      if (row.runningBalance !== null && row.runningBalance !== undefined) {
        return this.formatNumber(row.runningBalance);
      }
      return "";
    },

    formatTypeCellForExport(row) {
      if (!row || row.type === "initial_balance") return "";
      const expenseType = this.getPaymentExpenseTypeLabel(row);
      if (expenseType) return expenseType;
      return this.getStatementTypeLabel(row);
    },

    transactionDateMsForStatement(transaction) {
      if (!transaction || transaction.date == null || transaction.date === "") {
        return Number.POSITIVE_INFINITY;
      }
      const ms = new Date(transaction.date).getTime();
      return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
    },

    /**
     * Ticket #314: INV (shipping_pending) first, then the rest oldest → newest
     * within the same shipping. Used by UI and Excel so both match.
     */
    sortShippingTransactionsForStatement(transactions) {
      return [...(transactions || [])].sort((a, b) => {
        const aIsInvoice = a.type === "shipping_pending" ? 0 : 1;
        const bIsInvoice = b.type === "shipping_pending" ? 0 : 1;
        if (aIsInvoice !== bIsInvoice) return aIsInvoice - bIsInvoice;
        const dateDiff =
          this.transactionDateMsForStatement(a) -
          this.transactionDateMsForStatement(b);
        if (dateDiff !== 0) return dateDiff;
        return String(a._id || "").localeCompare(String(b._id || ""));
      });
    },

    /**
     * Filas del Excel en el orden en que llegaron, con el saldo acumulado ya
     * resuelto. Los anticipos pendientes salen aparte: van en su propia sección
     * al pie (ticket #314 / mock de Sol).
     */
    statementExportRows(transactions) {
      const accounting = [];
      const pendingPrepayments = [];
      (transactions || []).forEach((transaction) => {
        const runningBalance = Number(transaction.runningBalance) || 0;
        const row = {
          ...transaction,
          runningBalance,
        };
        if (
          !transaction.excludedFromTotals &&
          transaction.type !== "pending_prepayment"
        ) {
          row.accumulatedBalance = runningBalance;
        }
        if (transaction.type === "pending_prepayment") {
          pendingPrepayments.push(row);
        } else {
          accounting.push(row);
        }
      });
      return { accounting, pendingPrepayments };
    },

    /**
     * Bloques por shipping del Excel (ticket #314 / mock de Sol): solo líneas de
     * detalle, sin las cabeceras de grupo de la UI, con el balance del shipping
     * en la última línea de cada bloque.
     *
     * El orden, en cambio, es el de la tabla (ticket #386): lo que se baja tiene
     * que ser lo que se ve.
     */
    buildExportShippingBlocks(accounting) {
      // Mismo agrupado que `groupedTransactionsRows`: proformas en el orden en
      // que las manda el backend (la tabla tampoco las reordena) y, dentro de
      // cada una, los shippings por nombre. Antes se ordenaba comparando los
      // ObjectId de proforma, así que el Excel salía en un orden que en
      // pantalla no existe.
      const proformGroups = [];
      const proformGroupsByKey = new Map();

      accounting.forEach((transaction) => {
        const proformId = this.normalizeEntityId(this.getProformId(transaction));
        const shippingId =
          this.normalizeEntityId(this.getShippingId(transaction)) ||
          (transaction.type === "shipping_pending" && transaction._id
            ? String(transaction._id)
            : "");
        const proformKey = this.statementProformGroupKey(
          transaction,
          proformId,
          shippingId
        );
        const blockKey = shippingId
          ? `${proformKey}-shipping-${shippingId}`
          : `${proformKey}-without-shipping`;

        let proformGroup = proformGroupsByKey.get(proformKey);
        if (!proformGroup) {
          proformGroup = { blocks: [], blocksByKey: new Map() };
          proformGroupsByKey.set(proformKey, proformGroup);
          proformGroups.push(proformGroup);
        }

        let block = proformGroup.blocksByKey.get(blockKey);
        if (!block) {
          block = {
            key: blockKey,
            proformId,
            shippingId,
            // Etiqueta tomada de la primera fila del grupo, igual que la tabla:
            // es la que decide el orden entre shippings de la misma proforma.
            sortLabel: shippingId ? this.getShippingDisplay(transaction) : "-",
            transactions: [],
          };
          proformGroup.blocksByKey.set(blockKey, block);
          proformGroup.blocks.push(block);
        }
        block.transactions.push(transaction);
      });

      const blocks = [];
      proformGroups.forEach((proformGroup) => {
        proformGroup.blocks.sort((a, b) =>
          this.compareShippingNamesForStatement(a.sortLabel, b.sortLabel)
        );
        proformGroup.blocks.forEach((block) => {
          block.transactions = this.sortShippingTransactionsForStatement(
            block.transactions
          );
          block.summary = this.buildStatementGroupSummary(block.transactions);
          // Point 3: shipping balance on last line (closed block total), like UI collapse.
          block.shippingBalance = block.summary.balance;
          blocks.push(block);
        });
      });

      return blocks;
    },

    /**
     * Orden de shippings de la misma PI por nombre: sufijo tras el último guion.
     * Ej. OP5404/2026-. → OP5404/2026-1 → OP5404/2026-2
     * (punto / vacío primero; luego número; luego resto alfabético).
     */
    compareShippingNamesForStatement(nameA, nameB) {
      const parse = (raw) => {
        const s = String(raw || "").trim();
        if (!s || s === "-") {
          return { base: "", suffixKind: 2, suffixNum: Number.POSITIVE_INFINITY, suffixText: "" };
        }
        const idx = s.lastIndexOf("-");
        if (idx < 0) {
          return { base: s.toLowerCase(), suffixKind: 0, suffixNum: -1, suffixText: "" };
        }
        const base = s.slice(0, idx).toLowerCase();
        const suffix = s.slice(idx + 1).trim();
        if (suffix === "" || suffix === ".") {
          return { base, suffixKind: 0, suffixNum: -1, suffixText: suffix };
        }
        if (/^\d+$/.test(suffix)) {
          return { base, suffixKind: 1, suffixNum: parseInt(suffix, 10), suffixText: suffix };
        }
        return { base, suffixKind: 2, suffixNum: Number.POSITIVE_INFINITY, suffixText: suffix.toLowerCase() };
      };
      const a = parse(nameA);
      const b = parse(nameB);
      const baseCmp = a.base.localeCompare(b.base);
      if (baseCmp !== 0) return baseCmp;
      if (a.suffixKind !== b.suffixKind) return a.suffixKind - b.suffixKind;
      if (a.suffixKind === 1 && a.suffixNum !== b.suffixNum) {
        return a.suffixNum - b.suffixNum;
      }
      return a.suffixText.localeCompare(b.suffixText);
    },

    /**
     * Celda del Excel por clave de columna. Mismo valor que pinta la tabla; el
     * saldo inicial deja en blanco las columnas del shipping porque es una fila
     * sintética sin operación detrás.
     */
    statementExportCellValue(row, key, { shippingBalance = "" } = {}) {
      const isInitial = row.type === "initial_balance";
      switch (key) {
        case "contact":
          return isInitial ? "" : this.emptyExportCell(row.contact);
        case "proform":
          return isInitial
            ? ""
            : this.emptyExportCell(this.getProformDisplay(row));
        case "shipping":
          return this.formatShippingCellForExport(row);
        case "shippingDate":
          return isInitial
            ? ""
            : this.emptyExportCell(this.formatStatementShippingDate(row));
        case "etd":
          return isInitial
            ? ""
            : this.emptyExportCell(this.formatStatementEtd(row));
        case "eta":
          return isInitial
            ? ""
            : this.emptyExportCell(this.formatStatementEta(row));
        case "transitDays":
          return isInitial
            ? ""
            : this.emptyExportCell(this.formatStatementTransitDays(row));
        case "date":
          return isInitial ? "" : this.emptyExportCell(this.formatDate(row.date));
        case "reference":
          return this.emptyExportCell(row.reference);
        case "comment":
          return this.emptyExportCell(row.comment);
        case "file":
          return this.statementFileHref(row.file) ? this.$t("Yes") : "";
        case "status":
          return row.status
            ? this.emptyExportCell(this.getStatusLabel(row.status))
            : "";
        case "type":
          return this.emptyExportCell(this.formatTypeCellForExport(row));
        case "debit":
          return this.formatDebitCellForExport(row);
        case "credit":
          return this.formatCreditCellForExport(row);
        case "balance":
          return this.formatBalanceCellForExport(row);
        case "shippingBalance":
          return shippingBalance === "" || shippingBalance == null
            ? ""
            : this.formatStatementSigned(shippingBalance);
        case "accumulatedBalance":
          return this.formatAccumulatedBalanceCellForExport(row);
        default:
          return "";
      }
    },

    mapTransactionToExportCells(row, { shippingBalance = "" } = {}) {
      return this.statementExportColumns.map((col) =>
        this.statementExportCellValue(row, col.key, { shippingBalance })
      );
    },

    buildStatementExportPayload(transactions, initialBalance, exportTotals) {
      const columns = this.statementExportColumns;
      const header = columns.map((col) => col.label);
      const colCount = header.length;
      const emptyRow = () => Array(colCount).fill("");

      // Los estilos ya no pueden asumir posiciones fijas: el usuario elige qué
      // columnas ve y en qué orden. -1 = la columna está oculta y no se pinta.
      const columnIndex = (key) => columns.findIndex((col) => col.key === key);
      const shippingCol = columnIndex("shipping");
      const typeCol = columnIndex("type");
      const debitCol = columnIndex("debit");
      const creditCol = columnIndex("credit");
      const balanceCol = columnIndex("balance");
      const shippingBalanceCol = columnIndex("shippingBalance");

      const data = [header];
      const headerRowIndices = [0];
      const rowFillByIndex = {};
      const cellStyleByIndex = {};
      const outlineBorderRanges = [];
      const shippingBlockFills = ["FFF2F2F2", "FFFFFFFF"];
      const shippingFooterFill = "FFDDEBF7";
      const pendingSectionFill = "FFFFF2CC";
      const pendingTotalFill = "FFFFE699";

      const setCellFont = (rowIndex, colIndex, argb, bold = true) => {
        if (!argb || colIndex < 0) return;
        if (!cellStyleByIndex[rowIndex]) cellStyleByIndex[rowIndex] = {};
        cellStyleByIndex[rowIndex][colIndex] = {
          font: { bold, color: { argb } },
        };
      };

      const setRowCell = (row, colIndex, value) => {
        if (colIndex < 0) return;
        row[colIndex] = value;
      };

      const { accounting, pendingPrepayments } =
        this.statementExportRows(transactions);

      if (this.isStatementFlatExport) {
        // Espejo de la tabla ordenada o filtrada: filas planas en el orden que
        // mandó el backend, sin bloques ni balance por shipping.
        const flatStart = data.length;
        accounting.forEach((transaction) => {
          const excelRowIndex = data.length;
          data.push(this.mapTransactionToExportCells(transaction));
          const typeColor = this.statementExportTypeFontColor(transaction);
          setCellFont(excelRowIndex, shippingCol, typeColor);
          setCellFont(excelRowIndex, typeCol, typeColor);
        });
        if (data.length > flatStart) {
          outlineBorderRanges.push({
            startRow: flatStart,
            endRow: data.length - 1,
            startCol: 0,
            endCol: colCount - 1,
          });
        }
      } else {
        this.buildExportShippingBlocks(accounting).forEach((block, blockIndex) => {
          const blockFill =
            shippingBlockFills[blockIndex % shippingBlockFills.length];
          const rows = block.transactions || [];
          if (!rows.length) return;
          const blockStart = data.length;
          rows.forEach((transaction, index) => {
            const isLast = index === rows.length - 1;
            const excelRowIndex = data.length;
            data.push(
              this.mapTransactionToExportCells(transaction, {
                shippingBalance: isLast ? block.shippingBalance : "",
              })
            );
            rowFillByIndex[excelRowIndex] = isLast
              ? shippingFooterFill
              : blockFill;

            const typeColor = this.statementExportTypeFontColor(transaction);
            setCellFont(excelRowIndex, shippingCol, typeColor);
            setCellFont(excelRowIndex, typeCol, typeColor);
            if (isLast) {
              setCellFont(
                excelRowIndex,
                shippingBalanceCol,
                this.statementExportShippingBalanceFontColor(
                  block.shippingBalance
                )
              );
            }
          });
          outlineBorderRanges.push({
            startRow: blockStart,
            endRow: data.length - 1,
            startCol: 0,
            endCol: colCount - 1,
          });
        });
      }

      if (pendingPrepayments.length) {
        const sectionIndex = data.length;
        const pendingSectionRow = emptyRow();
        pendingSectionRow[0] = this.$t("Pending prepayment");
        data.push(pendingSectionRow);
        headerRowIndices.push(sectionIndex);

        const pendingStart = data.length;
        let pendingDebitTotal = 0;
        pendingPrepayments.forEach((transaction) => {
          const excelRowIndex = data.length;
          data.push(this.mapTransactionToExportCells(transaction));
          rowFillByIndex[excelRowIndex] = pendingSectionFill;
          pendingDebitTotal += Math.abs(Number(transaction.amount) || 0);
          const typeColor = this.statementExportTypeFontColor(transaction);
          setCellFont(excelRowIndex, shippingCol, typeColor);
          setCellFont(excelRowIndex, typeCol, typeColor);
          setCellFont(excelRowIndex, debitCol, "FFC00000");
        });

        // Second-plane total: informational only (not part of Current Balance / TOTALS).
        const pendingTotalIndex = data.length;
        const pendingTotalRow = emptyRow();
        pendingTotalRow[0] = `${this.$t("Pending prepayment")} (${this.$t(
          "Balance"
        )})`;
        setRowCell(
          pendingTotalRow,
          debitCol,
          this.formatStatementDebit(pendingDebitTotal)
        );
        data.push(pendingTotalRow);
        rowFillByIndex[pendingTotalIndex] = pendingTotalFill;
        setCellFont(pendingTotalIndex, 0, "FFC00000");
        setCellFont(pendingTotalIndex, debitCol, "FFC00000");

        outlineBorderRanges.push({
          startRow: pendingStart,
          endRow: pendingTotalIndex,
          startCol: 0,
          endCol: colCount - 1,
        });
      }

      const showInitialRow =
        this.statementView === "active" &&
        !this.filterProformId &&
        !this.filterShippingId &&
        initialBalance !== 0 &&
        initialBalance !== null &&
        initialBalance !== undefined;

      if (showInitialRow) {
        data.push(
          this.mapTransactionToExportCells({
            type: "initial_balance",
            _id: "initial-balance",
            name: this.$t("Initial Balance"),
            accumulatedBalance: initialBalance,
            totals: { balance: initialBalance },
          })
        );
      }

      data.push(emptyRow());
      // Por indice y no por literal: la cabecera depende de las columnas
      // visibles y una fila literal quedaria mas corta.
      const totalsRow = emptyRow();
      totalsRow[0] = this.$t("TOTALS");
      setRowCell(
        totalsRow,
        debitCol,
        this.formatStatementDebit(exportTotals?.totalDebits || 0)
      );
      setRowCell(
        totalsRow,
        creditCol,
        this.formatStatementCredit(exportTotals?.totalCredits || 0)
      );
      setRowCell(
        totalsRow,
        balanceCol,
        this.formatStatementSigned(exportTotals?.finalBalance || 0)
      );
      data.push(totalsRow);

      return {
        data,
        options: {
          headerRowIndices,
          rowFillByIndex,
          cellStyleByIndex,
          outlineBorderRanges,
        },
      };
    },

    /**
     * Excel del extracto. Vale para el de un contacto y para el general: el
     * endpoint y el catálogo de columnas ya se resuelven por alcance, y en el
     * general la columna Contacto sale en el archivo.
     */
    async exportToExcel() {
      try {
        this.exportingExcel = true;
        Swal.fire({
          title: this.$tn("Export to Excel"),
          text: this.$tn("Loading..."),
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await wencesApi.get(this.statementEndpoint, {
          params: this.buildExportQueryParams(),
        });

        if (!response.data?.success || !response.data?.data) {
          throw new Error(
            response.data?.error || "Failed to load data for export"
          );
        }

        const {
          transactions: allTransactions,
          initialBalance: exportInitialBalance,
          totals: exportTotals,
          contactName: exportContactName,
        } = response.data.data;

        if (!allTransactions || allTransactions.length === 0) {
          const showInitialRow =
            this.statementView === "active" &&
            !this.filterProformId &&
            !this.filterShippingId &&
            exportInitialBalance !== 0 &&
            exportInitialBalance !== null &&
            exportInitialBalance !== undefined;
          if (!showInitialRow) {
            Swal.close();
            Swal.fire({
              icon: "warning",
              title: this.$tn("No Data"),
              text: this.$tn("No transactions to export"),
            });
            return;
          }
        }

        const { data, options } = this.buildStatementExportPayload(
          allTransactions,
          exportInitialBalance,
          exportTotals
        );

        const safeName = (
          exportContactName ||
          this.contactName ||
          (this.isGeneralStatement ? "all_contacts" : "contact")
        )
          .replace(/[^a-zA-Z0-9-_]/g, "_")
          .substring(0, 50);
        const viewSuffix =
          this.statementView === "archived"
            ? "_archived"
            : this.statementView === "all"
              ? "_all"
              : "";
        const fileName = `account_statement_${safeName}${viewSuffix}_${new Date().toISOString().split("T")[0]}`;

        Swal.close();
        await ExportService.exportDataToExcel(
          data,
          fileName,
          this.$t("Account Statement"),
          options
        );

        Swal.fire({
          icon: "success",
          title: this.$tn("Export Successful"),
          text: this.$tn("Data exported successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error exporting contact account statement to Excel:", error);
        Swal.close();
        Swal.fire({
          icon: "error",
          title: this.$tn("Export Error"),
          text:
            error.response?.data?.error ||
            error.message ||
            this.$tn("Failed to export data"),
        });
      } finally {
        this.exportingExcel = false;
      }
    },

    /**
     * Movimientos / Archivados / Ocultas / Todo como una sola selección: al
     * elegir un tab se apaga el otro eje, que antes se combinaban (se podía
     * estar en Archivados y en Ocultas a la vez).
     */
    selectStatementTab(tab) {
      if (this.statementTab === tab) return;
      if (tab === "hidden") {
        this.hiddenView = true;
        this.setStatementView("active", { force: true });
        return;
      }
      this.hiddenView = false;
      this.setStatementView(tab, { force: true });
    },

    setStatementView(view, { force = false } = {}) {
      if (!force && this.statementView === view) return;
      this.statementView = view;
      this.filterProformId = "";
      this.filterShippingId = "";
      this.filterProformLabel = "";
      this.filterShippingLabel = "";
      this.hoverProformId = "";
      this.hoverShippingId = "";
      this.currentPage = 1;
      this.clearRowSelection();
      this.loadAccountStatement();
    },

    rowShippingId(row) {
      if (!row) return "";
      const fromShipping = this.normalizeEntityId(this.getShippingId(row));
      if (fromShipping) return fromShipping;
      if (row.type === "shipping_group" && row.entityId) {
        return this.normalizeEntityId(row.entityId);
      }
      if (row.type === "shipping_pending" && row._id) {
        return String(row._id);
      }
      return "";
    },
    /** Clave de línea: `_id` del movimiento, como en el listado general. */
    rowLineKey(row) {
      if (
        !row ||
        row.type === "initial_balance" ||
        row.type === "proform_group" ||
        row.type === "shipping_group"
      ) {
        return "";
      }
      if (row._id != null && row._id !== "") return String(row._id);
      return this.rowShippingId(row);
    },
    lineKeysOf(transactions) {
      const keys = new Set();
      (transactions || []).forEach((transaction) => {
        const key = this.rowLineKey(transaction);
        if (key) keys.add(key);
      });
      return [...keys];
    },
    /** Filas de grupo (PI / orden) representan las líneas que agrupan. */
    rowHideKeys(row) {
      if (Array.isArray(row && row.lineKeys)) return row.lineKeys;
      const key = this.rowLineKey(row);
      return key ? [key] : [];
    },
    rowSelectionKeys(row) {
      const selectable = this.selectableRowKeySet;
      return this.rowHideKeys(row).filter((key) => selectable.has(String(key)));
    },
    /**
     * Datos por clave usados sin la fila en pantalla: la selección puede incluir
     * páginas no cargadas (seleccionar todo) y aun así ocultar o pedir pago.
     */
    mergeRowMeta(rows) {
      (rows || []).forEach((row) => {
        const key = this.rowLineKey(row);
        if (!key) return;
        const payable = this.isPayableExpenseRow(row);
        this.$set(this.rowMetaByKey, key, {
          payable,
          unpaid: payable ? this.getRowRequestableAmount(row) : 0,
          requested: this.getRowRequestedAmount(row),
          fullyRequested: this.isFullyRequestedExpenseRow(row),
          modelName: row.type || "",
          shipping: this.rowShippingId(row),
        });
      });
    },
    hidePayloadsForKeys(keys) {
      const wanted = [...new Set((keys || []).map(String).filter(Boolean))];
      return wanted.map((key) => {
        const meta = this.rowMetaByKey[key] || {};
        return {
          scopeType: SCOPE_GENERAL_ACCOUNT_STATEMENT,
          rowKey: key,
          modelName: meta.modelName || "",
          shipping: meta.shipping || "",
        };
      });
    },
    isRowSelected(row) {
      const keys = this.rowSelectionKeys(row);
      if (!keys.length) return false;
      const selected = new Set(this.selectedRowKeys.map(String));
      return keys.every((key) => selected.has(String(key)));
    },
    isRowSelectionIndeterminate(row) {
      const keys = this.rowSelectionKeys(row);
      if (keys.length < 2) return false;
      const selected = new Set(this.selectedRowKeys.map(String));
      const hits = keys.filter((key) => selected.has(String(key))).length;
      return hits > 0 && hits < keys.length;
    },
    toggleRowSelection(row) {
      const keys = this.rowSelectionKeys(row).map(String);
      if (!keys.length) return;
      const selected = new Set(this.selectedRowKeys.map(String));
      const unselect = keys.every((key) => selected.has(key));
      keys.forEach((key) => {
        if (unselect) {
          selected.delete(key);
        } else {
          selected.add(key);
        }
      });
      this.selectedRowKeys = [...selected];
    },
    /** Filas marcables de un lote: con ocultar activo, todas; si no, gastos por pagar. */
    isRowSelectable(row) {
      if (this.rowHideEnabled) return true;
      return this.showPayableSelection && this.isPayableExpenseRow(row);
    },
    selectableKeysFrom(rows) {
      const keys = new Set();
      (rows || []).forEach((row) => {
        const key = this.rowLineKey(row);
        if (!key || !this.isRowSelectable(row)) return;
        keys.add(key);
      });
      return [...keys];
    },
    /** Seleccionar todo abarca el filtro completo, no solo las páginas cargadas. */
    async fetchAllSelectableRowKeys() {
      const totalItems = this.pagination?.totalItems || 0;
      if (totalItems > 0 && this.transactions.length >= totalItems) {
        return this.selectableRowKeys;
      }
      const params = this.buildQueryParams({
        page: 1,
        limit: totalItems || 999999,
      });
      const response = await wencesApi.get(this.statementEndpoint, { params });
      if (!response.data.success) return this.selectableRowKeys;
      const rows = response.data.data.transactions || [];
      this.mergeRowMeta(rows);
      return this.selectableKeysFrom(rows);
    },
    /** La casilla del encabezado es de la tabla principal: no toca las proformas marcadas. */
    async toggleSelectAllRows() {
      const proformSupplierKeys = this.selectedRowKeys.filter((key) =>
        this.proformSupplierRowKeySet.has(String(key))
      );
      if (this.isAllRowsSelected) {
        this.selectedRowKeys = proformSupplierKeys;
        return;
      }
      if (this.selectingAllRows) return;
      this.selectingAllRows = true;
      try {
        this.selectedRowKeys = [
          ...new Set([
            ...(await this.fetchAllSelectableRowKeys()),
            ...proformSupplierKeys,
          ]),
        ];
      } catch (error) {
        console.error("Error selecting all account statement rows:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("Error"),
          text: this.$tn("An error occurred while selecting records."),
        });
      } finally {
        this.selectingAllRows = false;
      }
    },
    clearRowSelection() {
      this.selectedRowKeys = [];
      this.paymentExtraParams = {};
    },
    isProformSupplierRowSelected(row) {
      const key = this.rowLineKey(row);
      return !!key && this.selectedRowKeys.some((k) => String(k) === key);
    },
    toggleProformSupplierRowSelection(row) {
      const key = this.rowLineKey(row);
      if (!key) return;
      this.selectedRowKeys = this.isProformSupplierRowSelected(row)
        ? this.selectedRowKeys.filter((k) => String(k) !== key)
        : [...this.selectedRowKeys, key];
    },
    /**
     * Fila pintada como oculta: todo el tab Ocultas, o la marca por fila que
     * manda el backend cuando el tab Todo mezcla visibles y ocultas.
     */
    isStatementRowHidden(row) {
      if (!this.rowHideEnabled) return false;
      if (this.hiddenView) return true;
      return !!(row && row.hiddenInView);
    },
    toggleRowHide(row) {
      return this.applyRowHide(this.rowHideKeys(row), {
        action: this.isStatementRowHidden(row) ? "unhide" : "hide",
      });
    },
    hideOrShowSelected() {
      return this.applyRowHide(this.hideableSelectedRowKeys);
    },
    async applyRowHide(keys, { action: forcedAction } = {}) {
      if (!this.rowHideEnabled || this.rowHideBusy) return;
      const rows = this.hidePayloadsForKeys(keys).filter((row) => row.rowKey);
      if (!rows.length) return;
      this.rowHideBusy = true;
      try {
        const action =
          forcedAction || (this.hiddenView ? "unhide" : "hide");
        for (let i = 0; i < rows.length; i += 200) {
          await bulkPerViewRowHide({
            action,
            rows: rows.slice(i, i + 200),
          });
        }
        this.clearRowSelection();
        await this.loadAccountStatement();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: this.$tn("Error"),
          text: error.response?.data?.error || this.$tn("Error"),
        });
      } finally {
        this.rowHideBusy = false;
      }
    },

    getArchiveProformId(row) {
      const pid = this.normalizeEntityId(this.getProformId(row));
      return pid || "";
    },

    async confirmRestoreRow(row) {
      const proformId = this.getArchiveProformId(row);
      if (!proformId) return;
      // El endpoint de restaurar es por contacto: en el extracto general el
      // contacto lo pone la fila, no la vista.
      const rowContactId =
        this.contactId || this.normalizeEntityId(row && row.contactId);
      if (!rowContactId) return;
      const confirm = await Swal.fire({
        title: this.$tn("accountStatementRestoreConfirmTitle"),
        text: this.$tn("accountStatementRestoreConfirmText"),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$tn("accountStatementRestoreOperation"),
        cancelButtonText: this.$tn("cancel"),
      });
      if (!confirm.isConfirmed) return;
      try {
        await wencesApi.post(
          `/api/financial/account-statement/unarchive-line/${rowContactId}`,
          { proformId }
        );
        await Swal.fire({
          icon: "success",
          title: this.$tn("accountStatementRestoredOk"),
          timer: 1800,
          showConfirmButton: false,
        });
        await this.loadAccountStatement();
      } catch (e) {
        const msg =
          (e.response && e.response.data && e.response.data.error) ||
          e.message ||
          "";
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: msg,
        });
      }
    },

    async refreshData() {
      await this.loadAccountStatement();
    },

    async loadLists() {
      try {
        const response = await wencesApi.get("/api/gotrading/lists");

        if (response.data) {
          this.lists = response.data;
        }
      } catch (error) {
        console.error("Error loading lists:", error);
      }
    },

    async applyFilters() {
      // Reset to first page when applying filters
      this.currentPage = 1;
      this.clearHoverHighlight();
      await this.loadAccountStatement();
    },

    /** ¿La columna se puede ordenar? Espejo de la lista del backend. */
    isStatementColumnSortable(col) {
      return !!col && this.statementSortableColumns.includes(col.key);
    },

    /**
     * Ciclo del orden en la cabecera: asc → desc → sin orden (vuelve al orden
     * cronológico agrupado).
     */
    async toggleStatementSort(col) {
      if (!this.isStatementColumnSortable(col)) return;
      const key = col.key;
      if (this.statementSortColumn !== key) {
        this.statementSortColumn = key;
        this.statementSortDirection = "asc";
      } else if (this.statementSortDirection === "asc") {
        this.statementSortDirection = "desc";
      } else {
        this.statementSortColumn = "";
        this.statementSortDirection = "asc";
      }
      this.currentPage = 1;
      this.clearHoverHighlight();
      await this.loadAccountStatement();
    },

    isStatementColumnFilterActive(columnKey) {
      return isFilterSpecActive(
        this.columnFilters && this.columnFilters[columnKey]
      );
    },

    serializeActiveColumnFilters() {
      return serializeColumnFilterSpecs(this.columnFilters);
    },

    openStatementColumnFilter(columnKey) {
      const col = this.statementFilterableColumns.find((c) => c.key === columnKey);
      if (!col) return;
      this.columnFilterPopupKey = columnKey;
      this.columnFilterPopupOpen = true;
    },

    closeStatementColumnFilter() {
      this.columnFilterPopupOpen = false;
      this.columnFilterPopupKey = "";
    },

    async applyStatementColumnFilter(payload) {
      const key = this.columnFilterPopupKey;
      if (!key || !payload) {
        this.closeStatementColumnFilter();
        return;
      }
      const next = payload.spec || {};
      if (payload.active) {
        this.$set(this.columnFilters, key, {
          value: next.value,
          operator: next.operator || "=",
          start: next.start || "",
          end: next.end || "",
        });
      } else if (Object.prototype.hasOwnProperty.call(this.columnFilters, key)) {
        this.$delete(this.columnFilters, key);
      }

      this.closeStatementColumnFilter();
      this.currentPage = 1;
      this.clearHoverHighlight();
      this.clearRowSelection();
      await this.loadAccountStatement();
    },

    async clearEntityFilter() {
      this.filterProformId = "";
      this.filterShippingId = "";
      this.filterProformLabel = "";
      this.filterShippingLabel = "";
      this.currentPage = 1;
      this.clearHoverHighlight();
      await this.loadAccountStatement();
    },

    isStatementGroupCollapsed(groupKey) {
      return Boolean(this.collapsedStatementGroups[groupKey]);
    },

    toggleStatementGroup(groupKey) {
      this.$set(
        this.collapsedStatementGroups,
        groupKey,
        !this.isStatementGroupCollapsed(groupKey)
      );
    },

    toggleAllStatementGroups() {
      if (this.hasCollapsedStatementGroups) {
        this.collapsedStatementGroups = {};
        return;
      }

      this.collapsedStatementGroups =
        this.statementCollapsibleGroupKeys.reduce((collapsed, groupKey) => {
          collapsed[groupKey] = true;
          return collapsed;
        }, {});
    },

    buildStatementGroupSummary(transactions) {
      const rows = transactions || [];
      const uniqueValues = (values) =>
        Array.from(
          new Set(
            values
              .filter((value) => value !== null && value !== undefined && value !== "")
              .map((value) => String(value).trim())
              .filter(Boolean)
          )
        );
      const compactValues = (values) => {
        const unique = uniqueValues(values);
        if (!unique.length) return "-";
        const visible = unique.slice(0, 2).join(" / ");
        return unique.length > 2 ? `${visible} +${unique.length - 2}` : visible;
      };
      const dateRange = (values) => {
        const unique = uniqueValues(values).sort();
        if (!unique.length) return "-";
        const first = formatDateSync(unique[0], this.$t("N/A"));
        const last = formatDateSync(
          unique[unique.length - 1],
          this.$t("N/A")
        );
        return first === last ? first : `${first} – ${last}`;
      };
      const numericRange = (values) => {
        // Do not Number(null)/Number('') → 0; empty transit/days must stay "-".
        const numbers = values
          .filter((value) => value != null && value !== "")
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value));
        if (!numbers.length) return "-";
        const minimum = Math.min(...numbers);
        const maximum = Math.max(...numbers);
        return minimum === maximum ? String(minimum) : `${minimum}–${maximum}`;
      };
      let totalDebits = 0;
      let totalCredits = 0;

      rows.forEach((row) => {
        if (row.excludedFromTotals || row.type === "pending_prepayment") return;
        const amount = Math.abs(Number(row.amount) || 0);
        if (this.isStatementDebitRow(row)) totalDebits += amount;
        if (this.isStatementCreditRow(row)) totalCredits += amount;
      });

      const references = uniqueValues(rows.map((row) => row.reference));
      const comments = uniqueValues(rows.map((row) => row.comment));
      const shippingIds = uniqueValues(
        rows.map(
          (row) =>
            this.normalizeEntityId(this.getShippingId(row)) ||
            (row.type === "shipping_pending" && row._id ? String(row._id) : "")
        )
      );

      return {
        contacts: compactValues(rows.map((row) => row.contact)),
        shippingCount: shippingIds.length,
        shippingDate: dateRange(rows.map((row) => row.shippingOperationDate)),
        etd: dateRange(rows.map((row) => row.shippingEtd)),
        eta: dateRange(rows.map((row) => row.shippingEta)),
        transitDays: numericRange(rows.map((row) => row.shippingTransitDays)),
        date: dateRange(rows.map((row) => row.date)),
        references: compactValues(references),
        referencesTitle: references.join("\n"),
        commentCount: comments.length,
        commentsTitle: comments.join("\n"),
        fileCount: uniqueValues(
          rows.flatMap((row) => this.statementFileRefs(row.file))
        ).length,
        statuses: compactValues(
          rows.map((row) => (row.status ? this.getStatusLabel(row.status) : ""))
        ),
        types: compactValues(
          rows.map((row) => this.getPaymentExpenseTypeLabel(row))
        ),
        totalDebits,
        totalCredits,
        balance: totalDebits - totalCredits,
        accumulatedBalance: Number(rows[0]?.runningBalance) || 0,
      };
    },

    async toggleProformFilter(row) {
      const id = this.normalizeEntityId(this.getProformId(row));
      if (!id) return;
      if (this.filterProformId === id) {
        await this.clearEntityFilter();
        return;
      }
      this.filterProformId = id;
      this.filterProformLabel = this.getProformDisplay(row);
      this.filterShippingId = "";
      this.filterShippingLabel = "";
      this.currentPage = 1;
      this.clearHoverHighlight();
      await this.loadAccountStatement();
    },

    async toggleShippingFilter(row) {
      const id = this.normalizeEntityId(this.getShippingId(row));
      const shippingPendingId =
        row && row.type === "shipping_pending" && row._id
          ? String(row._id)
          : "";
      const effectiveId = id || shippingPendingId;
      if (!effectiveId) return;
      if (this.filterShippingId === effectiveId) {
        await this.clearEntityFilter();
        return;
      }
      this.filterShippingId = effectiveId;
      this.filterShippingLabel = this.getShippingDisplay(row);
      this.filterProformId = "";
      this.filterProformLabel = "";
      this.currentPage = 1;
      this.clearHoverHighlight();
      await this.loadAccountStatement();
    },

    clearHoverHighlight() {
      this.hoverProformId = "";
      this.hoverShippingId = "";
    },

    /** Clear hover highlight only when pointer leaves the row (not when moving between Proform/Shipping cells). */
    onStatementTransactionRowLeave(event) {
      const next = event.relatedTarget;
      const rowEl = event.currentTarget;
      if (next && typeof rowEl.contains === "function" && rowEl.contains(next)) {
        return;
      }
      this.clearHoverHighlight();
    },

    onHoverProformCell(row) {
      const id = this.normalizeEntityId(this.getProformId(row));
      if (!id) {
        this.clearHoverHighlight();
        return;
      }
      this.hoverProformId = id;
      this.hoverShippingId = "";
    },

    onHoverShippingCell(row) {
      let id = this.normalizeEntityId(this.getShippingId(row));
      if (!id && row && row.type === "shipping_pending" && row._id) {
        id = String(row._id);
      }
      if (!id) {
        this.clearHoverHighlight();
        return;
      }
      this.hoverShippingId = id;
      this.hoverProformId = "";
    },

    normalizeEntityId(val) {
      if (val == null || val === "") return "";
      if (typeof val === "object" && val._id) return String(val._id);
      return String(val).trim();
    },

    rowHighlightClass(row) {
      if (!row || row.type === "initial_balance") return "";
      const pid = this.normalizeEntityId(this.getProformId(row));
      const sidRaw = this.normalizeEntityId(this.getShippingId(row));
      const sid =
        sidRaw ||
        (row.type === "shipping_pending" && row._id ? String(row._id) : "");
      if (this.hoverProformId && pid && pid === this.hoverProformId) {
        return "as-row-related";
      }
      if (this.hoverShippingId && sid && sid === this.hoverShippingId) {
        return "as-row-related";
      }
      return "";
    },

    openProformInNewTab(row) {
      const proformId = this.normalizeEntityId(this.getProformId(row));
      if (!proformId) return;
      window.open(`/dynamic-model/proform/view/${proformId}`, "_blank");
    },

    openShippingInNewTab(row) {
      if (!row) return;
      if (row.type === "shipping_pending" && row._id) {
        window.open(`/dynamic-model/shipping/view/${row._id}`, "_blank");
        return;
      }
      const shippingId = this.normalizeEntityId(this.getShippingId(row));
      if (!shippingId) return;
      window.open(`/dynamic-model/shipping/view/${shippingId}`, "_blank");
    },
    
    debounceSearch() {
      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      
      // Set new timer to search after 500ms of no typing
      this.searchDebounceTimer = setTimeout(() => {
        this.applySearchFilter();
      }, 500);
    },
    
    async applySearchFilter() {
      // Reset to first page when searching
      this.currentPage = 1;
      // Reload data from server with search filter
      await this.loadAccountStatement();
    },
    
    async clearSearch() {
      // Clear debounce timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
      
      this.searchText = '';
      // Reset to first page when clearing search
      this.currentPage = 1;
      // Reload data from server without search filter
      await this.loadAccountStatement();
    },



    // Navigation methods
    viewTransaction(transaction) {
      if (!transaction) return;
      
      // Handle proform type first (doesn't require _id)
      if (transaction.type === "proform") {
        // For proform rows, navigate to the proform view
        if (transaction.proform || transaction.proformId) {
          const proformId = transaction.proform || transaction.proformId;
          window.open(`/dynamic-model/proform/view/${proformId}`, '_blank');
        }
        return;
      }
      
      // For other types, require _id
      if (!transaction._id) return;
      
      if (transaction.type === "payment") {
        // For payments, navigate to the associated proform or shipping
        if (transaction.shipping) {
          window.open(
            `/dynamic-model/shipping/view/${transaction.shipping}`,
            '_blank'
          );
        } else if (transaction.proform) {
          window.open(
            `/dynamic-model/proform/view/${transaction.proform}`,
            '_blank'
          );
        } else {
          // Fallback to payment if no proform/shipping
          const paymentId = transaction.parentPayment || transaction._id;
          window.open(`/dynamic-model/payment/view/${paymentId}`, '_blank');
        }
      } else if (transaction.type === "collect") {
        // For collects, navigate to the associated proform or shipping
        if (transaction.shipping) {
          window.open(
            `/dynamic-model/shipping/view/${transaction.shipping}`,
            '_blank'
          );
        } else if (transaction.proform) {
          window.open(
            `/dynamic-model/proform/view/${transaction.proform}`,
            '_blank'
          );
        } else {
          // Fallback to collect if no proform/shipping
          const collectId = transaction.parentCollect || transaction._id;
          window.open(`/dynamic-model/collect/view/${collectId}`, '_blank');
        }
      } else if (transaction.type === "shipping_pending") {
        window.open(`/dynamic-model/shipping/view/${transaction._id}`, '_blank');
      } else if (transaction.type === "invoice_shipper") {
        // For invoice_shipper, navigate to the shipping using the shipping field
        window.open(
          `/dynamic-model/shipping/view/${transaction.shipping}`,
          '_blank'
        );
      } else if (transaction.type === "expense") {
        // For expense, navigate to the shipping using the shipping field
        window.open(
          `/dynamic-model/shipping/view/${transaction.shipping}`,
          '_blank'
        );
      } else if (transaction.type === "credit_note") {
        // For credit_note, navigate to the shipping using the shipping field
        window.open(
          `/dynamic-model/shipping/view/${transaction.shipping}`,
          '_blank'
        );
      } else if (transaction.type === "debit_note") {
        // For debit_note, navigate to the shipping using the shipping field
        window.open(
          `/dynamic-model/shipping/view/${transaction.shipping}`,
          '_blank'
        );
      }
    },

    viewContact(transaction) {
      if (transaction && transaction.contactId) {
        window.open(
          `/dynamic-model/Contact/view/${transaction.contactId}`,
          '_blank'
        );
      } else if (transaction && transaction.contact) {
        // If we only have the contact name, we might need to search for the contact ID
        // For now, we'll show a message or try to find it
        Swal.fire({
          icon: "info",
          title: this.$tn("Contact Information"),
          text: `${this.$tn("Contact")}: ${transaction.contact}`,
          footer: this.$tn("Contact ID not available for navigation"),
        });
      }
    },

    viewPaymentOrCollect(transaction) {
      if (transaction && transaction._id) {
        if (transaction.type === "payment") {
          // For payments, use parentPayment if available, otherwise use _id
          const paymentId = transaction.parentPayment || transaction._id;
          window.open(`/dynamic-model/payment/view/${paymentId}`, '_blank');
        } else if (transaction.type === "collect") {
          // For collects, use parentCollect if available, otherwise use _id
          const collectId = transaction.parentCollect || transaction._id;
          window.open(`/dynamic-model/collect/view/${collectId}`, '_blank');
        }
      }
    },

    getProformId(row) {
      if (!row || !row.proform) return "";
      if (typeof row.proform === "object") {
        return row.proform._id || row.proform.id || "";
      }
      return row.proform;
    },

    getShippingId(row) {
      if (!row || !row.shipping) return "";
      if (typeof row.shipping === "object") {
        return row.shipping._id || row.shipping.id || "";
      }
      return row.shipping;
    },

    looksLikeMongoObjectId(value) {
      return /^[a-f0-9]{24}$/i.test(String(value || "").trim());
    },

    /**
     * Anticipo de las filas de un grupo. Las cabeceras de grupo se arman en el front a
     * partir de sus transacciones, así que el dato que estampa el backend en cada
     * transacción hay que subirlo a la cabecera. Todas las filas del grupo son de la
     * misma Proforma / embarque, por eso alcanza con la primera que lo traiga.
     */
    statementRowsAdvance(transactions, key) {
      if (!Array.isArray(transactions)) return null;
      const withAdvance = transactions.find((t) => t && t[key]);
      return withAdvance ? withAdvance[key] : null;
    },

    /**
     * Ticket #363 — aviso de anticipo pendiente junto al número de Proforma / embarque,
     * con el mismo criterio que los listados dinámicos. Los montos los manda el backend
     * en `proformAdvance` / `shippingAdvance` (ver accountStatementPendingAdvance.js),
     * solo para Duntower: es un requisito de ese tenant.
     */
    showProformPrepaymentWarning(row) {
      if (!row || !isDuntowerTenant()) return false;
      if (this.getProformDisplay(row) === "-") return false;
      return hasMissingAssociatedPrepayment(row.proformAdvance);
    },

    showShippingPrepaymentWarning(row) {
      if (!row || !isDuntowerTenant()) return false;
      if (this.getShippingDisplay(row) === "-") return false;
      return hasMissingAssociatedPrepayment(row.shippingAdvance);
    },

    getProformDisplay(row) {
      if (!row) return "-";
      if (row.proformName && !this.looksLikeMongoObjectId(row.proformName)) {
        return row.proformName;
      }
      if (row.proformNumber && !this.looksLikeMongoObjectId(row.proformNumber)) {
        return row.proformNumber;
      }
      // Never show raw Mongo ids as the PI / proforma label (UI + Excel).
      return "-";
    },

    getShippingDisplay(row) {
      if (!row) return "-";
      if (row.shippingName) return row.shippingName;
      if (row.description && row.description !== "-") return row.description;
      const shippingId = this.getShippingId(row);
      return shippingId || "-";
    },

    // Utility methods
    getTransactionTypeLabel(type) {
      const typeLabels = {
        payment: this.$t("Payment"),
        collect: this.$t("Collection"),
        expense: this.$t("Expense"),
        refund: this.$t("Refund"),
        shipping_pending: this.$t("Invoice Client"),
        pending_prepayment: this.$t("Pending prepayment"),
        invoice_shipper: this.$t("Invoice Shipper"),
        credit_note: this.$t("Credit note"),
        debit_note: this.$t("Debit note"),
      };
      return typeLabels[type] || type;
    },

    getTypeLabel(type) {
      if (!type) return "-";

      // Si el tipo ya viene mapeado del backend (con label)
      if (typeof type === "object" && type.label) {
        return type.label;
      }

      // Si es solo un ID, buscar en las listas cargadas
      const id = typeof type === "object" && type._id ? type._id : type;
      const idStr = String(id);

      // Buscar en la lista "typeofpayment"
      const list = this.lists?.find((l) => l.type === "typeofpayment");
      if (list && list.values) {
        const option = list.values.find(
          (opt) => opt && opt._id != null && String(opt._id) === idStr
        );
        return option ? option.label : id;
      }

      return id;
    },

    getTransactionTypeClass(type) {
      const typeClasses = {
        payment: "bg-danger",
        collect: "bg-success",
        expense: "bg-warning",
        refund: "bg-info",
        shipping_pending: "bg-info",
        pending_prepayment: "bg-warning",
        invoice_shipper: "bg-info",
        credit_note: "bg-success",
        debit_note: "bg-danger",
      };
      return typeClasses[type] || "bg-secondary";
    },

    getStatusLabel(status) {
      const statusLabels = {
        pending: this.$t("Pending"),
        completed: this.$t("Received"),
        paid: this.$t("Paid"),
        cancelled: this.$t("Cancelled"),
        failed: this.$t("Failed"),
        emitted: this.$t("Emitted"),
      };
      return statusLabels[status] || status;
    },

    getStatusClass(status) {
      const statusClasses = {
        pending: "badge bg-warning",
        completed: "badge bg-success",
        paid: "badge bg-success",
        cancelled: "badge bg-secondary",
        failed: "badge bg-danger",
        emitted: "badge bg-info",
      };
      return statusClasses[status] || "badge bg-secondary";
    },

    getStatusTooltip(status) {
      const tooltips = {
        pending: this.$t("Transaction is pending"),
        completed: this.$t("Transaction is completed"),
        paid: this.$t("Payment has been made"),
        cancelled: this.$t("Transaction was cancelled"),
        failed: this.$t("Transaction failed"),
        emitted: this.$t("Invoice has been emitted"),
      };
      return tooltips[status] || this.$t("Unknown status");
    },

    getStatusIcon(status) {
      const icons = {
        pending: "fas fa-clock",
        completed: "fas fa-check-circle",
        paid: "fas fa-credit-card",
        cancelled: "fas fa-times-circle",
        failed: "fas fa-exclamation-triangle",
        emitted: "fas fa-file-invoice",
      };
      return icons[status] || "fas fa-question-circle";
    },

    isStatementDebitRow(row) {
      return isAccountStatementDebitRow(row, { includePendingPrepayment: true });
    },
    isStatementCreditRow(row) {
      return isAccountStatementCreditRow(row);
    },
    statementDebitDisplayAmount(row) {
      return accountStatementDebitDisplayAmount(row);
    },
    formatNumber(number) {
      // Siempre mostrar decimales para valores monetarios en account statement
      // Incluir 0 para que también se formatee correctamente
      return formatNumberSync(number || 0, null, true);
    },

    /** Absolute formatted amount with a leading minus (Debit column). */
    formatStatementDebit(amount) {
      return `-${this.formatNumber(Math.abs(Number(amount) || 0))}`;
    },

    /** Absolute formatted amount with a leading plus (Credit column). */
    formatStatementCredit(amount) {
      return `+${this.formatNumber(Math.abs(Number(amount) || 0))}`;
    },

    /** Signed amount for Balance / Accumulated Balance (+/− by value). */
    formatStatementSigned(amount) {
      const n = Number(amount);
      if (!Number.isFinite(n)) {
        return this.formatNumber(amount);
      }
      if (n < 0) {
        return `-${this.formatNumber(Math.abs(n))}`;
      }
      return `+${this.formatNumber(Math.abs(n))}`;
    },

    formatDate(dateString) {
      if (dateString == null || dateString === "") return "—";
      return formatDateSync(dateString, "—");
    },

    formatStatementShippingDate(row) {
      const raw = row && row.shippingOperationDate;
      return raw ? formatDateSync(raw, this.$t("N/A")) : "—";
    },

    formatStatementEtd(row) {
      const raw = row && row.shippingEtd;
      return raw ? formatDateSync(raw, this.$t("N/A")) : "—";
    },

    formatStatementEta(row) {
      const raw = row && row.shippingEta;
      return raw ? formatDateSync(raw, this.$t("N/A")) : "—";
    },

    formatStatementTransitDays(row) {
      const raw = row && row.shippingTransitDays;
      if (raw == null || raw === "") return "—";
      const n = Number(raw);
      return Number.isFinite(n) ? String(n) : "—";
    },

    // Calculation methods
    getTotalDebits() {
      // Use backend totals which include both payments and shipping_pending
      return this.totals.totalDebits || 0;
    },

    getTotalCredits() {
      // Use backend totals which include collects
      return this.totals.totalCredits || 0;
    },

    getFinalBalance() {
      // Use backend calculated final balance
      return this.totals.finalBalance || 0;
    },

    getRowUnpaidAmount(row) {
      if (!row) return 0;
      if (row.unpaidAmount != null) return Number(row.unpaidAmount) || 0;
      return Math.max(
        0,
        (Number(row.adjustedAmount ?? row.amount) || 0) -
          (Number(row.applied) || 0)
      );
    },

    /** Ya pedido y sin pagar: sigue debiéndose, pero no se puede volver a pedir. */
    getRowRequestedAmount(row) {
      if (!row) return 0;
      return Number(row.requestedAmount) || 0;
    },

    /**
     * Lo que todavía se puede pedir. Debe coincidir con el descuento que hace
     * hook_defaultvalues al armar el popup: si acá sobra algo que allá no,
     * el modal se abre vacío y finanzas no entiende por qué.
     */
    getRowRequestableAmount(row) {
      if (!row) return 0;
      if (row.requestableAmount != null) return Number(row.requestableAmount) || 0;
      return Math.max(
        0,
        this.getRowUnpaidAmount(row) - this.getRowRequestedAmount(row)
      );
    },

    /** Gasto que se puede mandar a pagar: factura o proforma del proveedor. */
    isRequestableExpenseType(row) {
      return (
        !!row &&
        (row.type === "expense" || row.type === PROFORM_SUPPLIER_ROW_TYPE)
      );
    },

    isFullyRequestedExpenseRow(row) {
      if (!this.isRequestableExpenseType(row) || !row._id) return false;
      if (this.getRowUnpaidAmount(row) <= 0.009) return false;
      return this.getRowRequestableAmount(row) <= 0.009;
    },

    /** Líneas de un grupo (PI / embarque); para una fila suelta, ella misma. */
    statementRowLineMetas(row) {
      const keys = Array.isArray(row && row.lineKeys) ? row.lineKeys : [];
      return keys
        .map((key) => this.rowMetaByKey[String(key)])
        .filter(Boolean);
    },

    /**
     * El cartel también en los headers colapsados: si el grupo viene cerrado,
     * la línea marcada no se ve y el aviso se pierde.
     */
    isFullyRequestedRow(row) {
      if (!row) return false;
      if (row.type !== "proform_group" && row.type !== "shipping_group") {
        return this.isFullyRequestedExpenseRow(row);
      }
      const metas = this.statementRowLineMetas(row);
      if (metas.some((meta) => meta.payable)) return false;
      return metas.some((meta) => meta.fullyRequested);
    },

    requestedRowTooltip(row) {
      return this.$t("accountStatementAlreadyRequestedHint", {
        amount: this.formatNumber(this.rowRequestedAmountTotal(row)),
      });
    },

    rowRequestedAmountTotal(row) {
      if (!row) return 0;
      if (row.type !== "proform_group" && row.type !== "shipping_group") {
        return this.getRowRequestedAmount(row);
      }
      return this.statementRowLineMetas(row).reduce(
        (sum, meta) => sum + (Number(meta.requested) || 0),
        0
      );
    },

    isPayableExpenseRow(row) {
      if (!this.isRequestableExpenseType(row) || !row._id) return false;
      return this.getRowRequestableAmount(row) > 0.009;
    },

    openSendPaymentForSelected() {
      const ids = this.selectedPayableIds;
      if (!ids.length) {
        Swal.fire({
          icon: "warning",
          title: this.$tn("No Selection"),
          text: this.$tn("accountStatementSelectInvoiceToPay"),
        });
        return;
      }
      this.parentID = ids[0];
      this.parentModel = "shipping_expenses";
      // proformAdvance: las proformas del proveedor se piden por su anticipo
      // pendiente, el mismo monto que suma el botón.
      this.paymentExtraParams = {
        proformAdvance: 1,
        ...(ids.length > 1 ? { expenseIds: ids.join(",") } : {}),
      };
      this.showAddPaymentModal = true;
    },

    closeAddPaymentModal() {
      this.showAddPaymentModal = false;
      this.parentID = null;
      this.parentModel = null;
      this.paymentExtraParams = {};
    },

    async onPaymentSaved() {
      this.showAddPaymentModal = false;
      this.parentID = null;
      this.parentModel = null;
      this.paymentExtraParams = {};
      this.clearRowSelection();
      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Payment created successfully",
      });
      await this.loadAccountStatement();
    },
  },
};
</script>

<style scoped>
.as-m-toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.as-m-toolbar__row {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.as-m-search {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  padding: 0.35rem 0.6rem;
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 10px;
}

.as-m-search input {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--bs-body-color);
  background: transparent;
  border: 0;
  outline: 0;
}

.as-m-search button {
  padding: 0;
  color: var(--bs-secondary-color);
  background: transparent;
  border: 0;
}

.as-m-filters {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.25rem 1rem 0.75rem;
}

/* En la hoja: el título arriba y las dos fechas (desde / hasta) parejas abajo. */
.as-m-filters ::v-deep .balance-date-range {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem 0.5rem;
}

.as-m-filters ::v-deep .balance-date-range__title {
  grid-column: 1 / -1;
}

.as-m-filters ::v-deep .balance-date-range__date {
  width: 100%;
  min-width: 0;
}

.as-m-hint {
  margin-bottom: 0.6rem;
  font-size: 0.8rem;
  color: var(--bs-secondary-color);
}

.as-m-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.as-m-initial {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
  background-color: rgba(var(--bs-info-rgb), 0.1);
  border-radius: 10px;
}

/* Embarques y movimientos cuelgan de su grupo: sangría y una línea. */
.as-m-row--1,
.as-m-row--2 {
  padding-left: 0.5rem;
  border-left: 2px solid var(--bs-border-color);
}

.as-m-row--1 {
  margin-left: 0.5rem;
}

.as-m-row--2 {
  margin-left: 1.1rem;
}

.as-m-title {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.as-m-check {
  width: 1.15rem;
  height: 1.15rem;
  margin: 0;
}

.account-statement {
  font-family: "Courier New", monospace;
}

/* Dentro del accordion de un contacto en la cuenta corriente general. */
.account-statement--embedded > .card {
  margin-bottom: 0;
  border-left: 3px solid var(--bs-primary, #0d6efd);
}

.account-statement-balance-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.column-header-toolbar-filter-btn {
  color: #0d6efd !important;
  text-decoration: none !important;
  opacity: 0.75;
  line-height: 1;
}
.column-header-toolbar-filter-btn:hover,
.column-header-toolbar-filter-btn:focus {
  color: #0d6efd !important;
  opacity: 1;
  text-decoration: none !important;
}
.column-header-toolbar-filter-btn--active {
  opacity: 1 !important;
  color: #ffc107 !important;
}

.cursor-pointer {
  cursor: pointer;
}

.hover\:bg-light:hover {
  background-color: #f8f9fa !important;
}

.table th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
}

.account-statement-table {
  width: 100%;
  min-width: 1865px;
  table-layout: fixed;
}

.account-statement-table th,
.account-statement-table td {
  overflow-wrap: anywhere;
}

.account-statement-table .as-col-selection {
  width: 48px;
}

.account-statement-table .as-col-contact {
  width: 170px;
}

.account-statement-table .as-col-proform {
  width: 155px;
}

.account-statement-table .as-col-shipping {
  width: 190px;
}

.account-statement-table .as-col-shipping-date,
.account-statement-table .as-col-etd,
.account-statement-table .as-col-eta,
.account-statement-table .as-col-date {
  width: 105px;
}

.account-statement-table .as-col-transit {
  width: 90px;
}

.account-statement-table .as-col-reference,
.account-statement-table .as-col-type {
  width: 130px;
}

.account-statement-table td.as-cell-reference {
  overflow: hidden;
  overflow-wrap: normal;
  vertical-align: middle;
}

.account-statement-table .as-reference-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
  cursor: help;
}

.account-statement-table .as-col-comment {
  width: 165px;
}

.account-statement-table .as-col-file {
  width: 115px;
}

.account-statement-table .as-col-status {
  width: 100px;
}

.account-statement-table .as-col-money {
  width: 115px;
}

.account-statement-table .as-col-accumulated {
  width: 140px;
}

.account-statement-table .as-col-actions {
  width: 110px;
}

/* Cabecera arrastrable para reordenar columnas. */
.account-statement-table th.as-th-draggable {
  position: relative;
  cursor: grab;
  user-select: none;
  padding-right: 14px;
}

.account-statement-table th.as-th-draggable:active {
  cursor: grabbing;
}

.account-statement-table th.as-th-draggable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.account-statement-table th.as-th-draggable.dragging {
  opacity: 0.5;
}

.account-statement-table th.as-th-draggable.drag-over {
  border-left: 2px solid #0d6efd;
}

/* Los controles de la cabecera no arrastran la columna. */
.account-statement-table th.as-th-draggable button {
  cursor: pointer;
}

/* Icono de ordenar: mismo criterio que en el listado dinámico — hereda el
   color de la cabecera y juega con la opacidad, así se ve tanto sobre fondo
   claro como sobre las cabeceras de color. */
.account-statement-table th .column-header-sort-icon {
  flex-shrink: 0;
  font-size: 1em;
  line-height: 1;
  padding: 2px 3px;
  border-radius: 3px;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

.account-statement-table th.as-th-draggable:hover .column-header-sort-icon {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.08);
}

.account-statement-table th .column-header-sort-icon:hover {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.18);
}

.account-statement-table th.sorted-asc .column-header-sort-icon,
.account-statement-table th.sorted-desc .column-header-sort-icon {
  opacity: 1;
}

.account-statement-table th.sorted-asc,
.account-statement-table th.sorted-desc {
  background-color: rgba(13, 110, 253, 0.08);
}

/* Tirador de ancho, en el borde derecho de cada cabecera. */
.as-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  border-right: 2px solid transparent;
  transition: border-color 0.15s ease;
  z-index: 20;
  touch-action: none;
}

.account-statement-table th.as-th-draggable:hover .as-resize-handle {
  border-right-color: rgba(13, 110, 253, 0.35);
}

.as-resize-handle:hover {
  border-right-color: #0d6efd;
}

/* El tema deja .table-responsive sin scroll en escritorio y la sección de la
   ficha recorta lo que sobra: la tabla scrollea sola para llegar a las
   columnas de la derecha (y la fijada queda quieta al scrollear). */
.as-table-scroll {
  overflow-x: auto;
}

/* ---- Columna fijada ----
   Una celda de tabla es transparente por defecto, así que sin fondo propio
   las columnas que scrollean se ven por debajo de la fijada. El color se toma
   del <tr> con --as-row-bg para que respete hover, filas de grupo, saldo
   inicial y resaltado, en vez de quedar blanco fijo. */
.account-statement-table thead tr,
.account-statement-table tbody tr {
  --as-row-bg: #fff;
}

.account-statement-table.table-hover > tbody > tr:hover {
  --as-row-bg: #ececec;
}

.account-statement-table tbody tr.statement-proform-group {
  --as-row-bg: #eef4ff;
}

.account-statement-table tbody tr.statement-shipping-group {
  --as-row-bg: #f8f9fa;
}

/* Saldo inicial: bg-info + bg-opacity-10 aplanado sobre blanco. */
.account-statement-table tbody tr.bg-info {
  --as-row-bg: #e7f8fd;
}

/* Resaltado al pasar por proforma/shipping: rgba(13,110,253,.12) sobre blanco. */
.account-statement-table tbody tr.as-row-related {
  --as-row-bg: #e0ebfe;
}

/* Fila oculta: la fijada también va en bordó, si no queda texto blanco sobre blanco. */
.account-statement-table tbody tr.per-view-row-hidden,
.account-statement-table.table-hover > tbody > tr.per-view-row-hidden:hover {
  --as-row-bg: #9b1b30;
}

.account-statement-table thead th.list-col-pinned,
.account-statement-table tbody td.list-col-pinned {
  background-color: var(--as-row-bg, #fff) !important;
  --bs-table-bg: var(--as-row-bg, #fff) !important;
  --bs-table-accent-bg: var(--as-row-bg, #fff) !important;
  background-clip: padding-box;
}

/* Última columna fijada: marca el corte con el área que scrollea. */
.account-statement-table .list-col-pinned-boundary {
  box-shadow: 3px 0 6px -2px rgba(0, 0, 0, 0.12);
}

.table tfoot td {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
  font-weight: bold;
}

.badge {
  font-size: 0.75em;
}

.card {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

/* Rows sharing the same hovered proforma/shipping */
.table tbody tr.as-row-related td {
  background-color: rgba(13, 110, 253, 0.12) !important;
}

.statement-group-row {
  cursor: pointer;
  user-select: none;
}

.statement-group-row td {
  border-left: 1px solid #ced4da;
  border-right: 1px solid #ced4da;
}

.statement-proform-group td {
  background-color: #eef4ff !important;
  border-top: 2px solid #8eb8f5;
}

.statement-shipping-group td {
  background-color: #f8f9fa !important;
}

.statement-group-row:focus {
  outline: 2px solid #0d6efd;
  outline-offset: -2px;
}

.statement-group-chevron {
  width: 0.9rem;
  color: #6c757d;
  text-align: center;
}

.statement-group-label {
  font-weight: 700;
}

.statement-collapsed-title {
  display: flex;
  min-width: 10rem;
  align-items: center;
  gap: 0.4rem;
}

.statement-shipping-collapsed-title {
  padding-left: 0.75rem;
}

.statement-summary-value {
  white-space: nowrap;
}

tr.per-view-row-hidden > td,
tr.statement-group-row.per-view-row-hidden td {
  background-color: #9b1b30 !important;
  color: #fff !important;
  border-color: #7a1526 !important;
}
tr.per-view-row-hidden .text-muted,
tr.per-view-row-hidden .text-success,
tr.per-view-row-hidden .text-danger,
tr.per-view-row-hidden .text-primary,
tr.per-view-row-hidden .text-info,
tr.per-view-row-hidden .text-secondary {
  color: #fff !important;
}
</style>
